import { formSet } from "../../gvc/testNewForm/utils";
import { validationFns } from "../../../components/form/validations";
import { Mk8sInput } from "../../../schema/types/mk8s/input/mk8s";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { store } from "../../../store";
import { inputToMk8s } from "./utils/inputToMk8s";
import { homeLink, request } from "../../../services/cpln";
import { v4 as uuidv4 } from "uuid";
import { processDryRunResponse } from "../../../mobxStores/dryRun/mk8s";

export function getValidator(id: string) {
  return async function validator(v: Mk8sInput): Promise<boolean | null> {
    // setup
    const validationCallId = uuidv4();

    formSet(id, `meta.valid`, false);
    formSet(id, `meta.validating`, true);
    formSet(id, `meta.validationCallId`, validationCallId);
    formSet(id, `errors`, {});
    formSet(id, `warnings`, {});

    const e: Partial<Record<string, { message: string }>> = {};
    const w: Partial<Record<string, { message: string }>> = {};

    function setError(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      e[path] = { message: message };
    }

    function setWarning(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      const currentWarning = w[path];
      if (currentWarning) {
        return;
      }
      w[path] = { message: message };
    }

    // setup end
    try {
      const fs = validationFns("Name");
      for (const f of [fs.required, fs.name]) {
        const r = f(v.name);
        if (r !== true) {
          setError(`name`, r);
        }
      }

      if (v.description.length > 250) {
        setError(`description`, `Description can be at most 250 characters`);
      }

      // tags start
      for (const tagIndexStr in v.tags) {
        const tagIndex = Number(tagIndexStr);
        const tag = v.tags[tagIndex];
        if (!tag.name) {
          setError(`tags.${tagIndex}.name`, "Tag key is required");
          setError(`tagsroot`, "Tag keys are required");
        }
      }

      if (v.tags.length > 50) {
        setError(`tagsroot`, "Tags cannot be more than 50 items");
      }
      // tags end

      if (v.provider === "aws") {
        if (!v.aws.deployRoleArn) {
          setError(`aws.deployRoleArn`, "Deploy Role ARN is required");
        } else {
          const regex = /^arn:(aws):iam::[0-9]+:role\/.+$/;
          if (!regex.test(v.aws.deployRoleArn)) {
            setError(`aws.deployRoleArn`, `Deploy Role ARN must be a valid match for regex: ${regex}`);
          }
        }

        if (!v.aws.region) {
          setError(`aws.region`, "Region is required");
        }

        if (v.aws.image.type === "none") {
          setError(`aws.image.recommended`, "Image is required");
          setError(`aws.image.exact`, "Image is required");
        }
        if (v.aws.image.type === "exact") {
          if (!v.aws.image.exact) {
            setError(`aws.image.exact`, "Image is required");
          } else {
            const regex = /^(ami-[0-9a-f]+)$|^(resolve:ssm:.+)$/;
            if (!regex.test(v.aws.image.exact)) {
              setError(`aws.image.exact`, `Exact must be a valid match for regex: ${regex}`);
            }
          }
        }
        if (v.aws.image.type === "recommended") {
          if (!v.aws.image.recommended) {
            setError(`aws.image.recommended`, "Image is required");
          }
        }

        if (!v.aws.vpcId) {
          setError(`aws.vpcId`, "VPC ID is required");
        } else {
          const regex = /^(vpc-[a-f0-9]+)$|^(resolve:ssm:.+)$/;
          if (!regex.test(v.aws.vpcId)) {
            setError(`aws.vpcId`, `VPC ID must be a valid match for regex: ${regex}`);
          }
        }

        if (v.aws.diskEncryptionKeyArn) {
          const regex = /^(arn:aws:kms:.+)$|^(resolve:ssm:.+)$/;
          if (!regex.test(v.aws.diskEncryptionKeyArn)) {
            setError(`aws.diskEncryptionKeyArn`, `Disk Encryption Key ARN must be a valid match for regex: ${regex}`);
          }
        }

        for (const indexStr in v.aws.securityGroupIds) {
          const index = Number(indexStr);
          const item = v.aws.securityGroupIds[index];

          if (!item.value) {
            setError(`aws.securityGroupIds.${index}.value`, `Value is required`);
            setError(`aws.securityGroupIdsroot`, `Values are required`);
          } else {
            const regex = /^(sg-[a-f0-9]+)$|^(resolve:ssm:.+)$/;
            if (!regex.test(item.value)) {
              setError(`aws.securityGroupIds.${index}.value`, `Value must be a valid match for regex: ${regex}`);
              setError(`aws.securityGroupIdsroot`, `Values must match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.aws.securityGroupIds) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _item = v.aws.securityGroupIds[_index];

            if (!!_item.value && !!item.value && _item.value === item.value) {
              setError(`aws.securityGroupIds.${index}.value`, `Value is not unique`);
              setError(`aws.securityGroupIdsroot`, `Values must be unique`);
            }
          }
        }

        if (v.aws.awsTags.length > 10) {
          setError(`aws.awsTagsroot`, `AWS Tags can have at most 10 items`);
        }

        for (const indexStr in v.aws.awsTags) {
          const index = Number(indexStr);
          const item = v.aws.awsTags[index];

          if (!item.name) {
            setError(`aws.awsTags.${index}.name`, `Key is required`);
            setError(`aws.awsTagsroot`, `Keys are required`);
          } else {
            if (item.name.length < 1) {
              setError(`aws.awsTags.${index}.name`, `Key must be at least 1 character`);
              setError(`aws.awsTagsroot`, `Keys must be at least 1 character`);
            }
            if (item.name.length > 128) {
              setError(`aws.awsTags.${index}.name`, `Key can be at most 128 characters`);
              setError(`aws.awsTagsroot`, `Keys can be at most 128 characters`);
            }
            const regex = /^[ a-zA-Z0-9=_+:./@-]+$/;
            if (!regex.test(item.name)) {
              setError(`aws.awsTags.${index}.name`, `Key must be a valid match for regex: ${regex}`);
              setError(`aws.awsTagsroot`, `Keys must match for regex: ${regex}`);
            }
          }

          if (!item.value) {
            setError(`aws.awsTags.${index}.value`, `Value is required`);
            setError(`aws.awsTagsroot`, `Values are required`);
          } else {
            if (item.value.length < 1) {
              setError(`aws.awsTags.${index}.value`, `Value must be at least 1 character`);
              setError(`aws.awsTagsroot`, `Values must be at least 1 character`);
            }
            if (item.value.length > 256) {
              setError(`aws.awsTags.${index}.value`, `Value can be at most 256 characters`);
              setError(`aws.awsTagsroot`, `Values can be at most 256 characters`);
            }
            const regex = /^[ a-zA-Z0-9=_+:./@-]+$/;
            if (!regex.test(item.value)) {
              setError(`aws.awsTags.${index}.value`, `Value must be a valid match for regex: ${regex}`);
              setError(`aws.awsTagsroot`, `Values must match for regex: ${regex}`);
            }
          }
        }

        for (const indexStr in v.aws.deployRoleChain) {
          const index = Number(indexStr);
          const item = v.aws.deployRoleChain[index];

          if (!item.roleArn) {
            setError(`aws.deployRoleChain.${index}.roleArn`, `Role ARN is required`);
            setError(`aws.deployRoleChainroot`, `Role ARN are required`);
          } else {
            const regex = /^arn:(aws):iam::[0-9]+:role\/.+$/;
            if (!regex.test(item.roleArn)) {
              setError(`aws.deployRoleChain.${index}.roleArn`, `Role ARN must be a valid match for regex: ${regex}`);
              setError(`aws.deployRoleChainroot`, `Role ARNs must match for regex: ${regex}`);
            }
          }

          if (item.sessionNamePrefix) {
            if (item.sessionNamePrefix.length > 50) {
              setError(`aws.deployRoleChain.${index}.sessionNamePrefix`, `Key can be at most 50 characters`);
              setError(`aws.deployRoleChainroot`, `Keys can be at most 50 characters`);
            }
            const regex = /[\w+=,.@-]*/;
            if (!regex.test(item.sessionNamePrefix)) {
              setError(
                `aws.deployRoleChain.${index}.sessionNamePrefix`,
                `Session Name Prefix must be a valid match for regex: ${regex}`,
              );
              setError(`aws.deployRoleChainroot`, `Session Name Prefixes must match for regex: ${regex}`);
            }
          }
        }

        for (const indexStr in v.aws.extraNodePolicies) {
          const index = Number(indexStr);
          const item = v.aws.extraNodePolicies[index];

          if (!item.value) {
            setError(`aws.extraNodePolicies.${index}.value`, `Value is required`);
            setError(`aws.extraNodePoliciesroot`, `Values are required`);
          } else {
            const regex = /^arn:(aws):iam::([0-9]+|aws):policy\/.+$/;
            if (!regex.test(item.value)) {
              setError(`aws.extraNodePolicies.${index}.value`, `Value must be a valid match for regex: ${regex}`);
              setError(`aws.extraNodePoliciesroot`, `Values must match for regex: ${regex}`);
            }
          }
        }

        if (v.aws.preInstallScript) {
          if (!v.aws.preInstallScript.startsWith("#!") && !v.aws.preInstallScript.startsWith("resolve:ssm:")) {
            setError(`aws.preInstallScript`, `Pre Install Script must start with either "#!" or with "resolve:ssm:"`);
          }
        }

        if (v.aws.networking.dnsForwarder) {
          if (v.aws.networking.dnsForwarder.length > 100) {
            setError(`aws.networking.dnsForwarder`, `DNS Forwarder can be at most 100 characters`);
          }
        }

        if (v.aws.autoscaler.expander.length > 3) {
          setError(`aws.autoscaler.expander`, `Expander list can have at most 3 selections`);
        }

        if (v.aws.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.aws.autoscaler.unneededTime)) {
            setError(
              `aws.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.aws.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.aws.autoscaler.unreadyTime)) {
            setError(
              `aws.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.aws.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.aws.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `aws.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.aws.nodePools) {
          const index = Number(indexStr);
          const np = v.aws.nodePools[index];

          if (!np.name) {
            setError(`aws.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`aws.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`aws.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.aws.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.aws.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`aws.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);

            const item = np.labels[_index];

            if (!item.name) {
              setError(`aws.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`aws.nodePools.${index}.labelsroot`, `Keys are required`);
            }
          }

          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);

            const item = np.taints[_index];

            if (!item.key) {
              setError(`aws.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`aws.nodePools.${index}.taintsroot`, `Keys are required`);
            }
          }

          if (np.minSize.length < 1) {
            setError(`aws.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`aws.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`aws.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`aws.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`aws.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (np.bootDiskSize.length > 0) {
            const xn = Number(np.bootDiskSize);
            if (xn < 20) {
              setError(`aws.nodePools.${index}.bootDiskSize`, `Boot Disk Size must be at least 20`);
            }
          }

          if (np.overrideImage.type === "exact") {
            if (!np.overrideImage.exact) {
              setError(`aws.nodePools.${index}.overrideImage.exact`, "Image is required");
            } else {
              const regex = /^(ami-[0-9a-f]+)$|^(resolve:ssm:.+)$/;
              if (!regex.test(np.overrideImage.exact)) {
                setError(
                  `aws.nodePools.${index}.overrideImage.exact`,
                  `Exact must be a valid match for regex: ${regex}`,
                );
              }
            }
          }
          if (np.overrideImage.type === "recommended" && !np.overrideImage.recommended) {
            setError(`aws.nodePools.${index}.overrideImage.recommended`, "Image is required");
          }

          if (np.onDemandBaseCapacity.length < 1) {
            setError(`aws.nodePools.${index}.onDemandBaseCapacity`, `On Demand Base Capacity is required`);
          }
          if (np.onDemandPercentageAboveBaseCapacity.length < 1) {
            setError(
              `aws.nodePools.${index}.onDemandPercentageAboveBaseCapacity`,
              `On Demand Percentage Above Base Capacity is required`,
            );
          }
          if (np.onDemandBaseCapacity.length > 0 && np.onDemandPercentageAboveBaseCapacity.length > 0) {
            const base = Number(np.onDemandBaseCapacity);
            const perc = Number(np.onDemandPercentageAboveBaseCapacity);
            const maxSizeNum = Number(np.maxSize);

            if (base < 0) {
              setError(`aws.nodePools.${index}.onDemandBaseCapacity`, `On Demand Base Capacity must be at least 0`);
            }
            if (base > maxSizeNum) {
              setError(
                `aws.nodePools.${index}.onDemandBaseCapacity`,
                `On Demand Base Capacity cannot be higher than Max Size`,
              );
            }
            if (perc < 0) {
              setError(
                `aws.nodePools.${index}.onDemandPercentageAboveBaseCapacity`,
                `On Demand Percentage Above Base Capacity must be at least 0`,
              );
            }
            if (perc > 100) {
              setError(
                `aws.nodePools.${index}.onDemandPercentageAboveBaseCapacity`,
                `On Demand Percentage Above Base Capacity can be at most 100`,
              );
            }
          }

          if (np.instanceTypes.length < 1) {
            setError(`aws.nodePools.${index}.instanceTypesroot`, `At least 1 Instance Type is required`);
          } else {
            for (const _indexStr in np.instanceTypes) {
              const _index = Number(_indexStr);
              const instanceType = np.instanceTypes[_index];

              if (!instanceType.value) {
                setError(`aws.nodePools.${index}.instanceTypes.${_index}`, `Instance Type is required`);
                setError(`aws.nodePools.${index}.instanceTypesroot`, `Instance Type values are required`);

                continue;
              }

              for (const __indexStr in np.instanceTypes) {
                const __index = Number(__indexStr);

                if (_index === __index) {
                  continue;
                }

                const _instanceType = np.instanceTypes[__index];

                if (!!_instanceType.value && !!instanceType.value && _instanceType.value === instanceType.value) {
                  setError(`aws.nodePools.${index}.instanceTypes.${_index}`, `Instance type is not unique`);
                  setError(`aws.nodePools.${index}.instanceTypesroot`, `Instance types are not unique`);
                }
              }
            }
          }

          if (np.subnetIds.length < 1) {
            setError(`aws.nodePools.${index}.subnetIdsroot`, `At least 1 Subnet ID is required`);
          } else {
            for (const _indexStr in np.subnetIds) {
              const _index = Number(_indexStr);
              const subnetId = np.subnetIds[_index];

              if (!subnetId.value) {
                setError(`aws.nodePools.${index}.subnetIds.${_index}`, `Subnet ID is required`);
                setError(`aws.nodePools.${index}.subnetIdsroot`, `Subnet ID values are required`);
              } else {
                const regex = /^(subnet-[a-f0-9]+)$|^(resolve:ssm:.+)$/;
                if (!regex.test(subnetId.value)) {
                  setError(
                    `aws.nodePools.${index}.subnetIds.${_index}`,
                    `Subnet ID must be a valid match for regex: ${regex}`,
                  );
                  setError(`aws.nodePools.${index}.subnetIdsroot`, `Subnet IDs must match regex: ${regex}`);
                }
              }
            }
          }

          if (np.extraSecurityGroupIds.length > 0) {
            for (const _indexStr in np.extraSecurityGroupIds) {
              const _index = Number(_indexStr);
              const item = np.extraSecurityGroupIds[_index];

              if (!item.value) {
                setError(
                  `aws.nodePools.${index}.extraSecurityGroupIds.${_index}`,
                  `Extra Security Group ID is required`,
                );
                setError(
                  `aws.nodePools.${index}.extraSecurityGroupIdsroot`,
                  `Extra Security Group ID values are required`,
                );
              } else {
                const regex = /^(sg-[a-f0-9]+)$|^(resolve:ssm:.+)$/;
                if (!regex.test(item.value)) {
                  setError(
                    `aws.nodePools.${index}.extraSecurityGroupIds.${_index}`,
                    `Extra Security Group ID must be a valid match for regex: ${regex}`,
                  );
                  setError(
                    `aws.nodePools.${index}.extraSecurityGroupIdsroot`,
                    `Extra Security Group IDs must match regex: ${regex}`,
                  );
                }
              }
            }
          }
        }
      }

      if (v.provider === "azure") {
        if (!v.azure.sdkSecretLink.link) {
          setError(`azure.sdkSecretLink`, "SDK Secret is required");
        } else {
          const parsedLink = parseLink(v.azure.sdkSecretLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`azure.sdkSecretLink`, parsedLink.errorMessage);
          }
        }

        if (!v.azure.location) {
          setError(`azure.location`, "Location is required");
        }

        if (!v.azure.subscriptionId) {
          setError(`azure.subscriptionId`, "Subscription ID is required");
        }

        if (!v.azure.resourceGroup) {
          setError(`azure.resourceGroup`, "Resource Group is required");
        }

        if (!v.azure.networkId) {
          setError(`azure.networkId`, "Network ID is required");
        }

        if (v.azure.image.type === "none") {
          setError(`azure.image.recommended`, "Image is required");
          setError(`azure.image.reference.publisher`, "Image is required");
          setError(`azure.image.reference.version`, "Image is required");
          setError(`azure.image.reference.sku`, "Image is required");
          setError(`azure.image.reference.offer`, "Image is required");
        }
        if (v.azure.image.type === "recommended") {
          if (!v.azure.image.recommended) {
            setError(`azure.image.recommended`, "Image is required");
          }
        }
        if (v.azure.image.type === "reference") {
          if (!v.azure.image.reference.offer) {
            setError(`azure.image.reference.offer`, "Offer is required");
          }
          if (!v.azure.image.reference.publisher) {
            setError(`azure.image.reference.publisher`, "Publisher is required");
          }
          if (!v.azure.image.reference.sku) {
            setError(`azure.image.reference.sku`, "SKU is required");
          }
          if (!v.azure.image.reference.version) {
            setError(`azure.image.reference.version`, "Version is required");
          }
        }

        if (v.azure.sshKeys.length < 1) {
          setError(`azure.sshKeysroot`, `At least 1 SSH Key is required`);
        } else {
          for (const indexStr in v.azure.sshKeys) {
            const index = Number(indexStr);
            const item = v.azure.sshKeys[index];

            if (!item.value) {
              setError(`azure.sshKeys.${index}.value`, `Value is required`);
              setError(`azure.sshKeysroot`, `SSH Key values are required`);
            } else {
              const validStarterStringsRaw: string[] = [
                "ssh-rsa",
                "ssh-ed25519",
                "ecdsa-sha2-nistp256",
                "ssh-ecdsa",
                "ssh-dsa",
              ];

              const validStarterStrings = validStarterStringsRaw.map((v) => `${v} `);

              let startsWithValidString = false;
              for (const validStarterString of validStarterStrings) {
                if (item.value.startsWith(validStarterString)) {
                  startsWithValidString = true;
                }
              }

              if (!startsWithValidString) {
                setError(
                  `azure.sshKeys.${index}.value`,
                  `Value must start with one of [${validStarterStringsRaw.join(", ")}] values`,
                );
                setError(
                  `azure.sshKeysroot`,
                  `Values must start with one of [${validStarterStringsRaw.join(", ")}] values`,
                );
              }
            }
          }
        }

        if (v.azure.tags.length > 10) {
          setError(`azure.tagsroot`, `Can have at most 10 tags`);
        }

        for (const indexStr in v.azure.tags) {
          const index = Number(indexStr);
          const item = v.azure.tags[index];

          if (!item.name) {
            setError(`azure.tags.${index}.name`, `Key is required`);
            setError(`azure.tagsroot`, `Keys are required`);
          } else {
            if (item.name.length < 1) {
              setError(`azure.tags.${index}.name`, `Key must be at least 1 character`);
              setError(`azure.tagsroot`, `Keys must be at least 1 character`);
            }
            if (item.name.length > 512) {
              setError(`azure.tags.${index}.name`, `Key can be at most 512 characters`);
              setError(`azure.tagsroot`, `Keys can be at most 512 characters`);
            }
            // Check for forbidden characters
            const badChars = "<>%&\\?/";
            for (const c of badChars) {
              if (item.name.includes(c)) {
                setError(`azure.tags.${index}.name`, `Key cannot contain any of: ${badChars}`);
                setError(`azure.tagsroot`, `Keys cannot contain any of: ${badChars}`);
                break;
              }
            }
            // Check for forbidden prefixes
            for (const prefix of ["microsoft", "azure", "windows"]) {
              if (item.name.toLowerCase().startsWith(prefix)) {
                setError(`azure.tags.${index}.name`, `Key cannot start with "${prefix}"`);
                setError(`azure.tagsroot`, `Keys cannot start with reserved prefixes`);
                break;
              }
            }
          }

          if (item.value.length > 0) {
            if (item.value.length > 256) {
              setError(`azure.tags.${index}.value`, `Value can be at most 256 characters`);
              setError(`azure.tagsroot`, `Values can be at most 256 characters`);
            }
          }
        }

        if (v.azure.preInstallScript) {
          if (!v.azure.preInstallScript.startsWith("#!") && !v.azure.preInstallScript.startsWith("resolve:ssm:")) {
            setError(`azure.preInstallScript`, `Pre Install Script must start with either "#!" or with "resolve:ssm:"`);
          }
        }

        if (v.azure.networking.dnsForwarder) {
          if (v.azure.networking.dnsForwarder.length > 100) {
            setError(`azure.networking.dnsForwarder`, `DNS Forwarder can be at most 100 characters`);
          }
        }

        if (v.azure.autoscaler.expander.length > 3) {
          setError(`azure.autoscaler.expander`, `Expander list can have at most 3 selections`);
        }

        if (v.azure.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.azure.autoscaler.unneededTime)) {
            setError(
              `azure.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.azure.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.azure.autoscaler.unreadyTime)) {
            setError(
              `azure.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.azure.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.azure.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `azure.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.azure.nodePools) {
          const index = Number(indexStr);
          const np = v.azure.nodePools[index];

          if (!np.name) {
            setError(`azure.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`azure.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`azure.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.azure.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.azure.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`azure.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);

            const item = np.labels[_index];

            if (!item.name) {
              setError(`azure.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`azure.nodePools.${index}.labelsroot`, `Keys are required`);
            }
          }

          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);

            const item = np.taints[_index];

            if (!item.key) {
              setError(`azure.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`azure.nodePools.${index}.taintsroot`, `Keys are required`);
            }
          }

          if (np.minSize.length < 1) {
            setError(`azure.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`azure.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`azure.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`azure.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`azure.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (!np.size) {
            setError(`azure.nodePools.${index}.size`, `Size is required`);
          }

          if (!np.subnetId) {
            setError(`azure.nodePools.${index}.subnetId`, `Subnet ID is required`);
          }

          if (np.bootDiskSize.length > 0) {
            const xn = Number(np.bootDiskSize);
            if (xn < 30) {
              setError(`azure.nodePools.${index}.bootDiskSize`, `Boot Disk Size must be at least 30`);
            }
          }

          if (np.overrideImage.type === "recommended") {
            if (!np.overrideImage.recommended) {
              setError(`azure.nodePools.${index}.overrideImage.recommended`, "Image is required");
            }
          }
          if (np.overrideImage.type === "reference") {
            if (!np.overrideImage.reference.offer) {
              setError(`azure.nodePools.${index}.overrideImage.reference.offer`, "Offer is required");
            }
            if (!np.overrideImage.reference.publisher) {
              setError(`azure.nodePools.${index}.overrideImage.reference.publisher`, "Publisher is required");
            }
            if (!np.overrideImage.reference.sku) {
              setError(`azure.nodePools.${index}.overrideImage.reference.sku`, "SKU is required");
            }
            if (!np.overrideImage.reference.version) {
              setError(`azure.nodePools.${index}.overrideImage.reference.version`, "Version is required");
            }
          }

          if (np.zones.length < 1) {
            setError(`azure.nodePools.${index}.zonesroot`, `At least 1 Zone is required`);
          } else {
            for (const zone of np.zones) {
              const zoneNum = Number(zone);
              if (![1, 2, 3].includes(zoneNum)) {
                setError(`azure.nodePools.${index}.zonesroot`, `Zones must be 1, 2, or 3`);
                break;
              }
            }
          }
        }
      }

      if (v.provider === "digitalocean") {
        if (!v.digitalocean.tokenSecretLink.link) {
          setError(`digitalocean.tokenSecretLink`, "Token Secret is required");
        } else {
          const parsedLink = parseLink(v.digitalocean.tokenSecretLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`digitalocean.tokenSecretLink`, parsedLink.errorMessage);
          }
        }

        if (!v.digitalocean.region) {
          setError(`digitalocean.region`, "Region is required");
        }

        if (!v.digitalocean.vpcId) {
          setError(`digitalocean.vpcId`, "VPC ID is required");
        }

        if (!v.digitalocean.image) {
          setError(`digitalocean.image`, "Image is required");
        }

        if (v.digitalocean.sshKeys.length < 1) {
          setError(`digitalocean.sshKeysroot`, "At least 1 SSH Key is required");
        }

        if (v.digitalocean.extraSshKeys.length > 0) {
          for (const indexStr in v.digitalocean.extraSshKeys) {
            const index = Number(indexStr);
            const item = v.digitalocean.extraSshKeys[index];

            if (!item.value) {
              setError(`digitalocean.extraSshKeys.${index}.value`, `Value is required`);
              setError(`digitalocean.extraSshKeysroot`, `SSH Key values are required`);
            } else {
              const validStarterStringsRaw: string[] = [
                "ssh-rsa",
                "ssh-ed25519",
                "ecdsa-sha2-nistp256",
                "ssh-ecdsa",
                "ssh-dsa",
              ];

              const validStarterStrings = validStarterStringsRaw.map((v) => `${v} `);

              let startsWithValidString = false;
              for (const validStarterString of validStarterStrings) {
                if (item.value.startsWith(validStarterString)) {
                  startsWithValidString = true;
                }
              }

              if (!startsWithValidString) {
                setError(
                  `digitalocean.extraSshKeys.${index}.value`,
                  `Value must start with one of [${validStarterStringsRaw.join(", ")}] values`,
                );
                setError(
                  `digitalocean.extraSshKeysroot`,
                  `Values must start with one of [${validStarterStringsRaw.join(", ")}] values`,
                );
              }
            }
          }
        }

        for (const indexStr in v.digitalocean.digitalOceanTags) {
          const index = Number(indexStr);
          const item = v.digitalocean.digitalOceanTags[index];

          if (!item.name) {
            setError(`digitalocean.digitalOceanTags.${index}.name`, `Key is required`);
            setError(`digitalocean.digitalOceanTagsroot`, `Keys are required`);
          } else {
            if (item.name.length > 254) {
              setError(`digitalocean.digitalOceanTags.${index}.name`, `Key can be at most 254 characters`);
              setError(`digitalocean.digitalOceanTagsroot`, `Keys can be at most 254 characters`);
            }

            const regex = /^[a-zA-Z0-9:_-]+$/;
            if (!regex.test(item.name)) {
              setError(`digitalocean.digitalOceanTags.${index}.name`, `Key must be a valid match for regex: ${regex}`);
              setError(`digitalocean.digitalOceanTagsroot`, `Keys must match regex: ${regex}`);
            }
          }
        }
        // Check for duplicate tags
        const doTagNames = v.digitalocean.digitalOceanTags.map((t: { name: string }) => t.name).filter(Boolean);
        const uniqueDoTagNames = new Set(doTagNames);
        if (doTagNames.length !== uniqueDoTagNames.size) {
          setError(`digitalocean.digitalOceanTagsroot`, `Tags must be unique`);
        }

        // Validate reservedIPs
        for (const indexStr in v.digitalocean.reservedIPs) {
          const index = Number(indexStr);
          const item = v.digitalocean.reservedIPs[index];

          if (!item.value) {
            setError(`digitalocean.reservedIPs.${index}.value`, `IP is required`);
            setError(`digitalocean.reservedIPsroot`, `IP values are required`);
          } else {
            // Validate IPv4 without CIDR
            const ipv4Regex =
              /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
            if (!ipv4Regex.test(item.value)) {
              setError(`digitalocean.reservedIPs.${index}.value`, `Must be a valid IPv4 address (no CIDR)`);
              setError(`digitalocean.reservedIPsroot`, `Must be valid IPv4 addresses`);
            }
          }
        }
        // Check for duplicate IPs
        const ipValues = v.digitalocean.reservedIPs.map((ip: { value: string }) => ip.value).filter(Boolean);
        const uniqueIPs = new Set(ipValues);
        if (ipValues.length !== uniqueIPs.size) {
          setError(`digitalocean.reservedIPsroot`, `Reserved IPs must be unique`);
        }

        if (v.digitalocean.preInstallScript) {
          if (
            !v.digitalocean.preInstallScript.startsWith("#!") &&
            !v.digitalocean.preInstallScript.startsWith("resolve:ssm:")
          ) {
            setError(
              `digitalocean.preInstallScript`,
              `Pre Install Script must start with either "#!" or with "resolve:ssm:"`,
            );
          }
        }

        if (v.digitalocean.networking.dnsForwarder) {
          if (v.digitalocean.networking.dnsForwarder.length > 100) {
            setError(`digitalocean.networking.dnsForwarder`, `DNS Forwarder can be at most 100 characters`);
          }
        }

        if (v.digitalocean.autoscaler.expander.length > 3) {
          setError(`digitalocean.autoscaler.expander`, `Expander list can have at most 3 selections`);
        }

        if (v.digitalocean.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.digitalocean.autoscaler.unneededTime)) {
            setError(
              `digitalocean.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.digitalocean.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.digitalocean.autoscaler.unreadyTime)) {
            setError(
              `digitalocean.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.digitalocean.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.digitalocean.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `digitalocean.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.digitalocean.nodePools) {
          const index = Number(indexStr);
          const np = v.digitalocean.nodePools[index];

          if (!np.name) {
            setError(`digitalocean.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`digitalocean.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`digitalocean.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.digitalocean.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.digitalocean.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`digitalocean.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);

            const item = np.labels[_index];

            if (!item.name) {
              setError(`digitalocean.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`digitalocean.nodePools.${index}.labelsroot`, `Keys are required`);
            }
          }

          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);

            const item = np.taints[_index];

            if (!item.key) {
              setError(`digitalocean.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`digitalocean.nodePools.${index}.taintsroot`, `Keys are required`);
            }
          }

          if (np.minSize.length < 1) {
            setError(`digitalocean.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`digitalocean.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`digitalocean.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`digitalocean.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`digitalocean.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (!np.dropletSize) {
            setError(`digitalocean.nodePools.${index}.dropletSize`, `Droplet Size is required`);
          }
        }
      }

      if (v.provider === "ephemeral") {
        if (!v.ephemeral.location) {
          setError(`ephemeral.location`, "Location is required");
        }

        for (const indexStr in v.ephemeral.nodePools) {
          const index = Number(indexStr);
          const np = v.ephemeral.nodePools[index];

          if (!np.name) {
            setError(`ephemeral.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`ephemeral.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`ephemeral.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.ephemeral.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.ephemeral.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`ephemeral.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);

            const item = np.labels[_index];

            if (!item.name) {
              setError(`ephemeral.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`ephemeral.nodePools.${index}.labelsroot`, `Keys are required`);
            }
          }

          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);

            const item = np.taints[_index];

            if (!item.key) {
              setError(`ephemeral.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`ephemeral.nodePools.${index}.taintsroot`, `Keys are required`);
            }
          }

          if (np.count.length < 1) {
            setError(`ephemeral.nodePools.${index}.count`, `Count is required`);
          } else {
            const xn = Number(np.count);
            if (xn < 0) {
              setError(`ephemeral.nodePools.${index}.count`, `Count must be at least 0`);
            }
            if (xn > 4) {
              setError(`ephemeral.nodePools.${index}.count`, `Count can be at most 4`);
            }
          }

          if (!np.arch) {
            setError(`ephemeral.nodePools.${index}.arch`, `Arch is required`);
          }
          if (!np.flavor) {
            setError(`ephemeral.nodePools.${index}.flavor`, `Flavor is required`);
          }
          if (!np.cpu) {
            setError(`ephemeral.nodePools.${index}.cpu`, `CPU is required`);
          } else {
            const cpuRegex = /^([0-9]+)(m|(\.[0-9]{1,3}))?$/;
            if (!cpuRegex.test(np.cpu)) {
              setError(
                `ephemeral.nodePools.${index}.cpu`,
                `CPU must be a valid K8s format (e.g., "500m", "2", "1.5")`,
              );
            }
          }
          if (!np.memory) {
            setError(`ephemeral.nodePools.${index}.memory`, `Memory is required`);
          } else {
            const memoryRegex = /^[0-9]+(\.[0-9]{1,3})?(G|M|k|Gi|Mi|Ki)?$/i;
            if (!memoryRegex.test(np.memory)) {
              setError(
                `ephemeral.nodePools.${index}.memory`,
                `Memory must be a valid K8s format (e.g., "512Mi", "2Gi")`,
              );
            }
          }
        }
      }

      if (v.provider === "gcp") {
        if (!v.gcp.saKeyLink.link) {
          setError(`gcp.saKeyLink`, "SA Key Secret is required");
        } else {
          const parsedLink = parseLink(v.gcp.saKeyLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`gcp.saKeyLink`, parsedLink.errorMessage);
          }
        }

        if (!v.gcp.projectId) {
          setError(`gcp.projectId`, "Project ID is required");
        }

        if (!v.gcp.region) {
          setError(`gcp.region`, "Region is required");
        }

        if (!v.gcp.network) {
          setError(`gcp.network`, "Network is required");
        }

        // Image validation
        if (v.gcp.image.type === "none") {
          setError(`gcp.image.recommended`, "Image is required");
          setError(`gcp.image.exact`, "Image is required");
          setError(`gcp.image.family.project`, "Image is required");
          setError(`gcp.image.family.family`, "Image is required");
        }
        if (v.gcp.image.type === "recommended") {
          if (!v.gcp.image.recommended) {
            setError(`gcp.image.recommended`, "Image is required");
          }
        }
        if (v.gcp.image.type === "exact") {
          if (!v.gcp.image.exact) {
            setError(`gcp.image.exact`, "Image is required");
          } else {
            const exactRegex = /^projects\/[a-z][-a-z0-9]{4,28}[a-z0-9]\/global\/images\/(?:family\/)?[a-z][-a-z0-9]{0,62}$/;
            if (!exactRegex.test(v.gcp.image.exact)) {
              setError(`gcp.image.exact`, `Image must be a valid GCP image path`);
            }
          }
        }
        if (v.gcp.image.type === "family") {
          if (!v.gcp.image.family.project) {
            setError(`gcp.image.family.project`, "Project is required");
          }
          if (!v.gcp.image.family.family) {
            setError(`gcp.image.family.family`, "Family is required");
          }
        }

        // Labels validation (GcpLabels)
        if (v.gcp.labels.length > 10) {
          setError(`gcp.labelsroot`, `Can have at most 10 labels`);
        }
        for (const indexStr in v.gcp.labels) {
          const index = Number(indexStr);
          const item = v.gcp.labels[index];

          if (!item.name) {
            setError(`gcp.labels.${index}.name`, `Key is required`);
            setError(`gcp.labelsroot`, `Keys are required`);
          } else {
            if (item.name.length > 63) {
              setError(`gcp.labels.${index}.name`, `Key can be at most 63 characters`);
              setError(`gcp.labelsroot`, `Keys can be at most 63 characters`);
            }
            const labelRegex = /^[a-z0-9][a-z0-9_-]*$/;
            if (!labelRegex.test(item.name)) {
              setError(`gcp.labels.${index}.name`, `Key must match regex: ${labelRegex}`);
              setError(`gcp.labelsroot`, `Keys must match regex: ${labelRegex}`);
            }
          }
          if (item.value && item.value.length > 63) {
            setError(`gcp.labels.${index}.value`, `Value can be at most 63 characters`);
            setError(`gcp.labelsroot`, `Values can be at most 63 characters`);
          }
          if (item.value) {
            const labelValueRegex = /^[a-z0-9][a-z0-9_-]*$/;
            if (!labelValueRegex.test(item.value)) {
              setError(`gcp.labels.${index}.value`, `Value must match regex: ${labelValueRegex}`);
              setError(`gcp.labelsroot`, `Values must match regex: ${labelValueRegex}`);
            }
          }
        }

        // Tags validation (GcpTags)
        if (v.gcp.tags.length > 61) {
          setError(`gcp.tagsroot`, `Can have at most 61 tags`);
        }
        for (const indexStr in v.gcp.tags) {
          const index = Number(indexStr);
          const item = v.gcp.tags[index];

          if (!item.value) {
            setError(`gcp.tags.${index}.value`, `Tag is required`);
            setError(`gcp.tagsroot`, `Tag values are required`);
          } else {
            if (item.value.length > 63) {
              setError(`gcp.tags.${index}.value`, `Tag can be at most 63 characters`);
              setError(`gcp.tagsroot`, `Tags can be at most 63 characters`);
            }
            const tagRegex = /^[a-z]([-a-z0-9]*[a-z0-9])?$/;
            if (!tagRegex.test(item.value)) {
              setError(`gcp.tags.${index}.value`, `Tag must match regex: ${tagRegex}`);
              setError(`gcp.tagsroot`, `Tags must match regex: ${tagRegex}`);
            }
          }
        }
        // Check for duplicate tags
        const gcpTagValues = v.gcp.tags.map((t: { value: string }) => t.value).filter(Boolean);
        const uniqueGcpTags = new Set(gcpTagValues);
        if (gcpTagValues.length !== uniqueGcpTags.size) {
          setError(`gcp.tagsroot`, `Tags must be unique`);
        }

        // Metadata validation (GcpMetadata)
        for (const indexStr in v.gcp.metadata) {
          const index = Number(indexStr);
          const item = v.gcp.metadata[index];

          if (!item.name) {
            setError(`gcp.metadata.${index}.name`, `Key is required`);
            setError(`gcp.metadataroot`, `Keys are required`);
          } else {
            if (item.name.length > 128) {
              setError(`gcp.metadata.${index}.name`, `Key can be at most 128 characters`);
              setError(`gcp.metadataroot`, `Keys can be at most 128 characters`);
            }
            const metadataKeyRegex = /^[a-zA-Z0-9_-]+$/;
            if (!metadataKeyRegex.test(item.name)) {
              setError(`gcp.metadata.${index}.name`, `Key must match regex: ${metadataKeyRegex}`);
              setError(`gcp.metadataroot`, `Keys must match regex: ${metadataKeyRegex}`);
            }
            if (item.name.startsWith("goog-")) {
              setError(`gcp.metadata.${index}.name`, `Key cannot start with "goog-"`);
              setError(`gcp.metadataroot`, `Keys cannot start with "goog-"`);
            }
            if (item.name.startsWith("google-")) {
              setError(`gcp.metadata.${index}.name`, `Key cannot start with "google-"`);
              setError(`gcp.metadataroot`, `Keys cannot start with "google-"`);
            }
          }
        }

        // Networking
        if (v.gcp.networking.dnsForwarder) {
          if (v.gcp.networking.dnsForwarder.length > 100) {
            setError(`gcp.networking.dnsForwarder`, `DNS Forwarder can be at most 100 characters`);
          }
        }

        // Autoscaler
        if (v.gcp.autoscaler.expander.length > 3) {
          setError(`gcp.autoscaler.expander`, `Expander list can have at most 3 selections`);
        }

        if (v.gcp.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.gcp.autoscaler.unneededTime)) {
            setError(
              `gcp.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.gcp.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.gcp.autoscaler.unreadyTime)) {
            setError(
              `gcp.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.gcp.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.gcp.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `gcp.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        // Node pools
        for (const indexStr in v.gcp.nodePools) {
          const index = Number(indexStr);
          const np = v.gcp.nodePools[index];

          if (!np.name) {
            setError(`gcp.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`gcp.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`gcp.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          // Check unique names
          for (const _indexStr in v.gcp.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.gcp.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`gcp.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          // Labels
          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`gcp.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`gcp.nodePools.${index}.labelsroot`, `Keys are required`);
            }
          }

          // Taints
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`gcp.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`gcp.nodePools.${index}.taintsroot`, `Keys are required`);
            }
          }

          if (!np.machineType) {
            setError(`gcp.nodePools.${index}.machineType`, `Machine Type is required`);
          }

          if (!np.zone) {
            setError(`gcp.nodePools.${index}.zone`, `Zone is required`);
          }

          if (!np.subnet) {
            setError(`gcp.nodePools.${index}.subnet`, `Subnet is required`);
          }

          if (np.bootDiskSize.length > 0) {
            const size = Number(np.bootDiskSize);
            if (size < 20) {
              setError(`gcp.nodePools.${index}.bootDiskSize`, `Boot Disk Size must be at least 20`);
            }
          }

          if (np.minSize.length < 1) {
            setError(`gcp.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`gcp.nodePools.${index}.maxSize`, `Max Size is required`);
          }

          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSize = Number(np.minSize);
            const maxSize = Number(np.maxSize);

            if (minSize < 0) {
              setError(`gcp.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSize > maxSize) {
              setError(`gcp.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSize < minSize) {
              setError(`gcp.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          // Override image validation
          if (np.overrideImage.type === "recommended") {
            if (!np.overrideImage.recommended) {
              setError(`gcp.nodePools.${index}.overrideImage.recommended`, "Image is required");
            }
          }
          if (np.overrideImage.type === "exact") {
            if (!np.overrideImage.exact) {
              setError(`gcp.nodePools.${index}.overrideImage.exact`, "Image is required");
            } else {
              const exactRegex =
                /^projects\/[a-z][-a-z0-9]{4,28}[a-z0-9]\/global\/images\/(?:family\/)?[a-z][-a-z0-9]{0,62}$/;
              if (!exactRegex.test(np.overrideImage.exact)) {
                setError(`gcp.nodePools.${index}.overrideImage.exact`, `Image must be a valid GCP image path`);
              }
            }
          }
          if (np.overrideImage.type === "family") {
            if (!np.overrideImage.family.project) {
              setError(`gcp.nodePools.${index}.overrideImage.family.project`, "Project is required");
            }
            if (!np.overrideImage.family.family) {
              setError(`gcp.nodePools.${index}.overrideImage.family.family`, "Family is required");
            }
          }
        }
      }

      if (v.provider === "generic") {
        if (!v.generic.location) {
          setError(`generic.location`, "Location is required");
        }

        if (v.generic.networking.dnsForwarder) {
          if (v.generic.networking.dnsForwarder.length > 100) {
            setError(`generic.networking.dnsForwarder`, `DNS Forwarder can be at most 100 characters`);
          }
        }

        for (const indexStr in v.generic.nodePools) {
          const index = Number(indexStr);
          const np = v.generic.nodePools[index];

          if (!np.name) {
            setError(`generic.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`generic.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`generic.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.generic.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.generic.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`generic.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);

            const item = np.labels[_index];

            if (!item.name) {
              setError(`generic.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`generic.nodePools.${index}.labelsroot`, `Keys are required`);
            }
          }

          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);

            const item = np.taints[_index];

            if (!item.key) {
              setError(`generic.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`generic.nodePools.${index}.taintsroot`, `Keys are required`);
            }
          }
        }
      }

      if (v.provider === "hetzner") {
        if (!v.hetzner.tokenSecretLink.link) {
          setError(`hetzner.tokenSecretLink`, "Token Secret is required");
        } else {
          const parsedLink = parseLink(v.hetzner.tokenSecretLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`hetzner.tokenSecretLink`, parsedLink.errorMessage);
          }
        }

        if (!v.hetzner.region) {
          setError(`hetzner.region`, "Region is required");
        }
        if (!v.hetzner.networkId) {
          setError(`hetzner.networkId`, "Network ID is required");
        }
        if (!v.hetzner.image) {
          setError(`hetzner.image`, "Image is required");
        }

        for (const indexStr in v.hetzner.floatingIPSelector) {
          const index = Number(indexStr);
          const item = v.hetzner.floatingIPSelector[index];

          if (!item.name) {
            setError(`hetzner.floatingIPSelector.${index}.name`, `Name is required`);
            setError(`hetzner.floatingIPSelectorroot`, `Names are required`);
          } else {
            if (item.name.includes(",")) {
              setError(`hetzner.floatingIPSelector.${index}.name`, `Name cannot contain comma`);
              setError(`hetzner.floatingIPSelectorroot`, `Names cannot contain comma`);
            }
            if (item.name.includes("=")) {
              setError(`hetzner.floatingIPSelector.${index}.name`, `Name cannot contain equals sign`);
              setError(`hetzner.floatingIPSelectorroot`, `Names cannot contain equals sign`);
            }
          }

          if (item.value) {
            if (item.value.includes(",")) {
              setError(`hetzner.floatingIPSelector.${index}.value`, `Value cannot contain comma`);
              setError(`hetzner.floatingIPSelectorroot`, `Values cannot contain comma`);
            }
            if (item.value.includes("=")) {
              setError(`hetzner.floatingIPSelector.${index}.value`, `Value cannot contain equals sign`);
              setError(`hetzner.floatingIPSelectorroot`, `Values cannot contain equals sign`);
            }
          }
        }

        for (const indexStr in v.hetzner.hetznerLabels) {
          const index = Number(indexStr);
          const item = v.hetzner.hetznerLabels[index];

          if (!item.name) {
            setError(`hetzner.hetznerLabels.${index}.name`, `Key is required`);
            setError(`hetzner.hetznerLabelsroot`, `Keys are required`);
          } else {
            if (item.name.length < 1) {
              setError(`hetzner.hetznerLabels.${index}.name`, `Key must be at least 1 character`);
              setError(`hetzner.hetznerLabelsroot`, `Keys must be at least 1 character`);
            }
            if (item.name.length > 63) {
              setError(`hetzner.hetznerLabels.${index}.name`, `Key can be at most 63 characters`);
              setError(`hetzner.hetznerLabelsroot`, `Keys can be at most 63 characters`);
            }
            const regex = /^[a-zA-Z0-9/_-]+$/;
            if (!regex.test(item.name)) {
              setError(`hetzner.hetznerLabels.${index}.name`, `Key must be a valid match for regex: ${regex}`);
              setError(`hetzner.hetznerLabelsroot`, `Keys must match for regex: ${regex}`);
            }
          }

          if (item.value.length > 255) {
            setError(`hetzner.hetznerLabels.${index}.value`, `Value can be at most 255 characters`);
            setError(`hetzner.hetznerLabelsroot`, `Values can be at most 255 characters`);
          }
        }

        // Check for duplicate hetznerLabels keys
        const hetznerLabelKeys = v.hetzner.hetznerLabels.map((l: any) => l.name).filter(Boolean);
        const uniqueHetznerLabelKeys = new Set(hetznerLabelKeys);
        if (hetznerLabelKeys.length !== uniqueHetznerLabelKeys.size) {
          setError(`hetzner.hetznerLabelsroot`, `Label keys must be unique`);
        }

        if (v.hetzner.preInstallScript) {
          if (!v.hetzner.preInstallScript.startsWith("#!") && !v.hetzner.preInstallScript.startsWith("resolve:ssm:")) {
            setError(
              `hetzner.preInstallScript`,
              `Pre Install Script must start with either "#!" or with "resolve:ssm:"`,
            );
          }
        }

        if (v.hetzner.networking.dnsForwarder) {
          if (v.hetzner.networking.dnsForwarder.length > 100) {
            setError(`hetzner.networking.dnsForwarder`, `DNS Forwarder can be at most 100 characters`);
          }
        }

        if (v.hetzner.autoscaler.expander.length < 1) {
          setError(`hetzner.autoscaler.expander`, `At least one expander must be selected`);
        }
        if (v.hetzner.autoscaler.expander.length > 3) {
          setError(`hetzner.autoscaler.expander`, `Expander list can have at most 3 selections`);
        }
        // Check for unique expanders
        const uniqueHetznerExpanders = new Set(v.hetzner.autoscaler.expander);
        if (v.hetzner.autoscaler.expander.length !== uniqueHetznerExpanders.size) {
          setError(`hetzner.autoscaler.expander`, `Expanders must be unique`);
        }

        if (v.hetzner.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.hetzner.autoscaler.unneededTime)) {
            setError(
              `hetzner.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.hetzner.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.hetzner.autoscaler.unreadyTime)) {
            setError(
              `hetzner.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.hetzner.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.hetzner.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `hetzner.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.hetzner.nodePools) {
          const index = Number(indexStr);
          const np = v.hetzner.nodePools[index];

          if (!np.name) {
            setError(`hetzner.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`hetzner.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`hetzner.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.hetzner.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.hetzner.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`hetzner.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`hetzner.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`hetzner.nodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(`hetzner.nodePools.${index}.labels.${_index}.value`, `Value can be at most 63 characters`);
            }
          }

          const npTaintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`hetzner.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`hetzner.nodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              npTaintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(`hetzner.nodePools.${index}.taints.${_index}.value`, `Value can be at most 63 characters`);
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `hetzner.nodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          // Check for unique taints
          const uniqueNpTaintKeys = new Set(npTaintKeys);
          if (npTaintKeys.length !== uniqueNpTaintKeys.size) {
            setError(`hetzner.nodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }

          if (np.minSize.length < 1) {
            setError(`hetzner.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`hetzner.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`hetzner.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`hetzner.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`hetzner.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (np.serverType.length < 1) {
            setError(`hetzner.nodePools.${index}.serverType`, `Server Type is required`);
          }
        }

        for (const indexStr in v.hetzner.dedicatedServerNodePools) {
          const index = Number(indexStr);
          const np = v.hetzner.dedicatedServerNodePools[index];

          if (!np.name) {
            setError(`hetzner.dedicatedServerNodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`hetzner.dedicatedServerNodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(
                `hetzner.dedicatedServerNodePools.${index}.name`,
                `Name must be a valid match for regex: ${regex}`,
              );
            }
          }

          for (const _indexStr in v.hetzner.dedicatedServerNodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.hetzner.dedicatedServerNodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`hetzner.dedicatedServerNodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`hetzner.dedicatedServerNodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`hetzner.dedicatedServerNodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(
                `hetzner.dedicatedServerNodePools.${index}.labels.${_index}.value`,
                `Value can be at most 63 characters`,
              );
            }
          }

          const dspTaintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`hetzner.dedicatedServerNodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`hetzner.dedicatedServerNodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              dspTaintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(
                `hetzner.dedicatedServerNodePools.${index}.taints.${_index}.value`,
                `Value can be at most 63 characters`,
              );
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `hetzner.dedicatedServerNodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          // Check for unique taints
          const uniqueDspTaintKeys = new Set(dspTaintKeys);
          if (dspTaintKeys.length !== uniqueDspTaintKeys.size) {
            setError(`hetzner.dedicatedServerNodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }
        }

        // Check for duplicate names across both pool types
        const allHetznerPoolNames: string[] = [];
        for (const np of v.hetzner.nodePools) {
          if (np.name) allHetznerPoolNames.push(np.name);
        }
        for (const np of v.hetzner.dedicatedServerNodePools) {
          if (np.name) allHetznerPoolNames.push(np.name);
        }
        const uniqueHetznerPoolNames = new Set(allHetznerPoolNames);
        if (allHetznerPoolNames.length !== uniqueHetznerPoolNames.size) {
          setError(`hetzner.nodePoolsroot`, `Node pool names must be unique across all pool types`);
          setError(`hetzner.dedicatedServerNodePoolsroot`, `Node pool names must be unique across all pool types`);
        }
      }

      if (v.provider === "lambdalabs") {
        if (!v.lambdalabs.tokenSecretLink.link) {
          setError(`lambdalabs.tokenSecretLink`, "Token Secret is required");
        } else {
          const parsedLink = parseLink(v.lambdalabs.tokenSecretLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`lambdalabs.tokenSecretLink`, parsedLink.errorMessage);
          }
        }

        if (!v.lambdalabs.region) {
          setError(`lambdalabs.region`, "Region is required");
        }

        if (!v.lambdalabs.sshKey) {
          setError(`lambdalabs.sshKey`, "SSH Key is required");
        }

        if (v.lambdalabs.preInstallScript) {
          if (
            !v.lambdalabs.preInstallScript.startsWith("#!") &&
            !v.lambdalabs.preInstallScript.startsWith("resolve:ssm:")
          ) {
            setError(
              `lambdalabs.preInstallScript`,
              `Pre Install Script must start with either "#!" or with "resolve:ssm:"`,
            );
          }
        }

        if (v.lambdalabs.autoscaler.expander.length < 1) {
          setError(`lambdalabs.autoscaler.expander`, `At least one expander must be selected`);
        }
        if (v.lambdalabs.autoscaler.expander.length > 3) {
          setError(`lambdalabs.autoscaler.expander`, `Expander list can have at most 3 selections`);
        }
        // Check for unique expanders
        const uniqueLambdalabsExpanders = new Set(v.lambdalabs.autoscaler.expander);
        if (v.lambdalabs.autoscaler.expander.length !== uniqueLambdalabsExpanders.size) {
          setError(`lambdalabs.autoscaler.expander`, `Expanders must be unique`);
        }

        if (v.lambdalabs.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.lambdalabs.autoscaler.unneededTime)) {
            setError(
              `lambdalabs.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.lambdalabs.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.lambdalabs.autoscaler.unreadyTime)) {
            setError(
              `lambdalabs.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.lambdalabs.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.lambdalabs.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `lambdalabs.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.lambdalabs.nodePools) {
          const index = Number(indexStr);
          const np = v.lambdalabs.nodePools[index];

          if (!np.name) {
            setError(`lambdalabs.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`lambdalabs.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`lambdalabs.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.lambdalabs.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.lambdalabs.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`lambdalabs.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`lambdalabs.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`lambdalabs.nodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(`lambdalabs.nodePools.${index}.labels.${_index}.value`, `Value can be at most 63 characters`);
            }
          }

          const llNpTaintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`lambdalabs.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`lambdalabs.nodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              llNpTaintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(`lambdalabs.nodePools.${index}.taints.${_index}.value`, `Value can be at most 63 characters`);
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `lambdalabs.nodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          // Check for unique taints
          const uniqueLlNpTaintKeys = new Set(llNpTaintKeys);
          if (llNpTaintKeys.length !== uniqueLlNpTaintKeys.size) {
            setError(`lambdalabs.nodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }

          if (np.minSize.length < 1) {
            setError(`lambdalabs.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`lambdalabs.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`lambdalabs.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`lambdalabs.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`lambdalabs.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (!np.instanceType) {
            setError(`lambdalabs.nodePools.${index}.instanceType`, `Instance Type is required`);
          }
        }

        for (const indexStr in v.lambdalabs.unmanagedNodePools) {
          const index = Number(indexStr);
          const np = v.lambdalabs.unmanagedNodePools[index];

          if (!np.name) {
            setError(`lambdalabs.unmanagedNodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`lambdalabs.unmanagedNodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`lambdalabs.unmanagedNodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.lambdalabs.unmanagedNodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.lambdalabs.unmanagedNodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`lambdalabs.unmanagedNodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`lambdalabs.unmanagedNodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`lambdalabs.unmanagedNodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(
                `lambdalabs.unmanagedNodePools.${index}.labels.${_index}.value`,
                `Value can be at most 63 characters`,
              );
            }
          }

          const llUnmanagedTaintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`lambdalabs.unmanagedNodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`lambdalabs.unmanagedNodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              llUnmanagedTaintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(
                `lambdalabs.unmanagedNodePools.${index}.taints.${_index}.value`,
                `Value can be at most 63 characters`,
              );
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `lambdalabs.unmanagedNodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          // Check for unique taints
          const uniqueLlUnmanagedTaintKeys = new Set(llUnmanagedTaintKeys);
          if (llUnmanagedTaintKeys.length !== uniqueLlUnmanagedTaintKeys.size) {
            setError(`lambdalabs.unmanagedNodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }
        }

        // Check for duplicate names across both pool types
        const allLambdalabsPoolNames: string[] = [];
        for (const np of v.lambdalabs.nodePools) {
          if (np.name) allLambdalabsPoolNames.push(np.name);
        }
        for (const np of v.lambdalabs.unmanagedNodePools) {
          if (np.name) allLambdalabsPoolNames.push(np.name);
        }
        const uniqueLambdalabsPoolNames = new Set(allLambdalabsPoolNames);
        if (allLambdalabsPoolNames.length !== uniqueLambdalabsPoolNames.size) {
          setError(`lambdalabs.nodePoolsroot`, `Node pool names must be unique across all pool types`);
          setError(`lambdalabs.unmanagedNodePoolsroot`, `Node pool names must be unique across all pool types`);
        }
      }

      if (v.provider === "linode") {
        if (!v.linode.tokenSecretLink.link) {
          setError(`linode.tokenSecretLink`, "Token Secret is required");
        } else {
          const parsedLink = parseLink(v.linode.tokenSecretLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`linode.tokenSecretLink`, parsedLink.errorMessage);
          }
        }

        if (!v.linode.region) {
          setError(`linode.region`, "Region is required");
        }

        if (!v.linode.vpcId) {
          setError(`linode.vpcId`, "VPC ID is required");
        }

        if (!v.linode.image) {
          setError(`linode.image`, "Image is required");
        }

        if (v.linode.preInstallScript) {
          if (!v.linode.preInstallScript.startsWith("#!") && !v.linode.preInstallScript.startsWith("resolve:ssm:")) {
            setError(
              `linode.preInstallScript`,
              `Pre Install Script must start with either "#!" or with "resolve:ssm:"`,
            );
          }
        }

        if (v.linode.networking.dnsForwarder) {
          if (v.linode.networking.dnsForwarder.length > 100) {
            setError(`linode.networking.dnsForwarder`, `DNS Forwarder can be at most 100 characters`);
          }
        }

        if (v.linode.autoscaler.expander.length < 1) {
          setError(`linode.autoscaler.expander`, `At least one expander must be selected`);
        }
        if (v.linode.autoscaler.expander.length > 3) {
          setError(`linode.autoscaler.expander`, `Expander list can have at most 3 selections`);
        }
        // Check for unique expanders
        const uniqueLinodeExpanders = new Set(v.linode.autoscaler.expander);
        if (v.linode.autoscaler.expander.length !== uniqueLinodeExpanders.size) {
          setError(`linode.autoscaler.expander`, `Expanders must be unique`);
        }

        // Check for unique authorizedUsers
        const authorizedUsers = v.linode.authorizedUsers?.map((u: any) => u.value).filter(Boolean) || [];
        const uniqueAuthorizedUsers = new Set(authorizedUsers);
        if (authorizedUsers.length !== uniqueAuthorizedUsers.size) {
          setError(`linode.authorizedUsersroot`, `Authorized users must be unique`);
        }

        // Check for unique authorizedKeys
        const authorizedKeys = v.linode.authorizedKeys?.map((k: any) => k.value).filter(Boolean) || [];
        const uniqueAuthorizedKeys = new Set(authorizedKeys);
        if (authorizedKeys.length !== uniqueAuthorizedKeys.size) {
          setError(`linode.authorizedKeysroot`, `Authorized keys must be unique`);
        }

        if (v.linode.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.linode.autoscaler.unneededTime)) {
            setError(
              `linode.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.linode.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.linode.autoscaler.unreadyTime)) {
            setError(
              `linode.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.linode.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.linode.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `linode.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.linode.nodePools) {
          const index = Number(indexStr);
          const np = v.linode.nodePools[index];

          if (!np.name) {
            setError(`linode.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`linode.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`linode.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.linode.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.linode.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`linode.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`linode.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`linode.nodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(`linode.nodePools.${index}.labels.${_index}.value`, `Value can be at most 63 characters`);
            }
          }

          const linodeTaintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`linode.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`linode.nodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              linodeTaintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(`linode.nodePools.${index}.taints.${_index}.value`, `Value can be at most 63 characters`);
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `linode.nodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          // Check for unique taints
          const uniqueLinodeTaintKeys = new Set(linodeTaintKeys);
          if (linodeTaintKeys.length !== uniqueLinodeTaintKeys.size) {
            setError(`linode.nodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }

          if (np.minSize.length < 1) {
            setError(`linode.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`linode.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`linode.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`linode.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`linode.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (!np.serverType) {
            setError(`linode.nodePools.${index}.serverType`, `Server Type is required`);
          }

          if (!np.subnetId) {
            setError(`linode.nodePools.${index}.subnetId`, `Subnet ID is required`);
          }
        }
      }

      if (v.provider === "oblivus") {
        if (!v.oblivus.tokenSecretLink.link) {
          setError(`oblivus.tokenSecretLink`, "Token Secret is required");
        } else {
          const parsedLink = parseLink(v.oblivus.tokenSecretLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`oblivus.tokenSecretLink`, parsedLink.errorMessage);
          }
        }

        if (!v.oblivus.datacenter) {
          setError(`oblivus.datacenter`, "Datacenter is required");
        }

        if (v.oblivus.preInstallScript) {
          if (!v.oblivus.preInstallScript.startsWith("#!") && !v.oblivus.preInstallScript.startsWith("resolve:ssm:")) {
            setError(
              `oblivus.preInstallScript`,
              `Pre Install Script must start with either "#!" or with "resolve:ssm:"`,
            );
          }
        }

        if (v.oblivus.autoscaler.expander.length < 1) {
          setError(`oblivus.autoscaler.expander`, `At least one expander must be selected`);
        }
        if (v.oblivus.autoscaler.expander.length > 3) {
          setError(`oblivus.autoscaler.expander`, `Expander list can have at most 3 selections`);
        }
        // Check for unique expanders
        const uniqueOblivusExpanders = new Set(v.oblivus.autoscaler.expander);
        if (v.oblivus.autoscaler.expander.length !== uniqueOblivusExpanders.size) {
          setError(`oblivus.autoscaler.expander`, `Expanders must be unique`);
        }

        // Check for unique sshKeys
        const oblivusSshKeys = v.oblivus.sshKeys?.map((k: any) => k.value).filter(Boolean) || [];
        const uniqueOblivusSshKeys = new Set(oblivusSshKeys);
        if (oblivusSshKeys.length !== uniqueOblivusSshKeys.size) {
          setError(`oblivus.sshKeysroot`, `SSH keys must be unique`);
        }

        if (v.oblivus.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.oblivus.autoscaler.unneededTime)) {
            setError(
              `oblivus.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.oblivus.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.oblivus.autoscaler.unreadyTime)) {
            setError(
              `oblivus.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.oblivus.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.oblivus.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `oblivus.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.oblivus.nodePools) {
          const index = Number(indexStr);
          const np = v.oblivus.nodePools[index];

          if (!np.name) {
            setError(`oblivus.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`oblivus.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`oblivus.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.oblivus.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.oblivus.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`oblivus.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`oblivus.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`oblivus.nodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(`oblivus.nodePools.${index}.labels.${_index}.value`, `Value can be at most 63 characters`);
            }
          }

          const oblivusNpTaintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`oblivus.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`oblivus.nodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              oblivusNpTaintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(`oblivus.nodePools.${index}.taints.${_index}.value`, `Value can be at most 63 characters`);
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `oblivus.nodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          // Check for unique taints
          const uniqueOblivusNpTaintKeys = new Set(oblivusNpTaintKeys);
          if (oblivusNpTaintKeys.length !== uniqueOblivusNpTaintKeys.size) {
            setError(`oblivus.nodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }

          if (np.minSize.length < 1) {
            setError(`oblivus.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`oblivus.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`oblivus.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`oblivus.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`oblivus.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (!np.flavor) {
            setError(`oblivus.nodePools.${index}.flavor`, `Flavor is required`);
          }
        }

        for (const indexStr in v.oblivus.unmanagedNodePools) {
          const index = Number(indexStr);
          const np = v.oblivus.unmanagedNodePools[index];

          if (!np.name) {
            setError(`oblivus.unmanagedNodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`oblivus.unmanagedNodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`oblivus.unmanagedNodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.oblivus.unmanagedNodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.oblivus.unmanagedNodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`oblivus.unmanagedNodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`oblivus.unmanagedNodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`oblivus.unmanagedNodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(
                `oblivus.unmanagedNodePools.${index}.labels.${_index}.value`,
                `Value can be at most 63 characters`,
              );
            }
          }

          const oblivusUnmanagedTaintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`oblivus.unmanagedNodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`oblivus.unmanagedNodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              oblivusUnmanagedTaintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(
                `oblivus.unmanagedNodePools.${index}.taints.${_index}.value`,
                `Value can be at most 63 characters`,
              );
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `oblivus.unmanagedNodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          // Check for unique taints
          const uniqueOblivusUnmanagedTaintKeys = new Set(oblivusUnmanagedTaintKeys);
          if (oblivusUnmanagedTaintKeys.length !== uniqueOblivusUnmanagedTaintKeys.size) {
            setError(`oblivus.unmanagedNodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }
        }

        // Check for duplicate names across both pool types
        const allOblivusPoolNames: string[] = [];
        for (const np of v.oblivus.nodePools) {
          if (np.name) allOblivusPoolNames.push(np.name);
        }
        for (const np of v.oblivus.unmanagedNodePools) {
          if (np.name) allOblivusPoolNames.push(np.name);
        }
        const uniqueOblivusPoolNames = new Set(allOblivusPoolNames);
        if (allOblivusPoolNames.length !== uniqueOblivusPoolNames.size) {
          setError(`oblivus.nodePoolsroot`, `Node pool names must be unique across all pool types`);
          setError(`oblivus.unmanagedNodePoolsroot`, `Node pool names must be unique across all pool types`);
        }
      }

      if (v.provider === "paperspace") {
        if (!v.paperspace.tokenSecretLink.link) {
          setError(`paperspace.tokenSecretLink`, "Token Secret is required");
        } else {
          const parsedLink = parseLink(v.paperspace.tokenSecretLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`paperspace.tokenSecretLink`, parsedLink.errorMessage);
          }
        }

        if (!v.paperspace.region) {
          setError(`paperspace.region`, "Region is required");
        }

        if (!v.paperspace.networkId) {
          setError(`paperspace.networkId`, "Network ID is required");
        }

        if (v.paperspace.preInstallScript) {
          if (
            !v.paperspace.preInstallScript.startsWith("#!") &&
            !v.paperspace.preInstallScript.startsWith("resolve:ssm:")
          ) {
            setError(
              `paperspace.preInstallScript`,
              `Pre Install Script must start with either "#!" or with "resolve:ssm:"`,
            );
          }
        }

        if (v.paperspace.autoscaler.expander.length < 1) {
          setError(`paperspace.autoscaler.expander`, `At least 1 expander is required`);
        } else if (v.paperspace.autoscaler.expander.length > 3) {
          setError(`paperspace.autoscaler.expander`, `Expander list can have at most 3 selections`);
        } else {
          const uniqueExpanders = new Set(v.paperspace.autoscaler.expander);
          if (uniqueExpanders.size !== v.paperspace.autoscaler.expander.length) {
            setError(`paperspace.autoscaler.expander`, `Expanders must be unique`);
          }
        }

        // Check userIds uniqueness
        const userIds = v.paperspace.userIds.map((u: { id: string; value: string }) => u.value).filter(Boolean);
        const uniqueUserIds = new Set(userIds);
        if (userIds.length !== uniqueUserIds.size) {
          setError(`paperspace.userIds`, `User IDs must be unique`);
        }

        // Collect all pool names for cross-type uniqueness check
        const allPoolNames: string[] = [];
        for (const np of v.paperspace.nodePools) {
          if (np.name) allPoolNames.push(np.name);
        }
        for (const np of v.paperspace.unmanagedNodePools) {
          if (np.name) allPoolNames.push(np.name);
        }

        if (v.paperspace.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.paperspace.autoscaler.unneededTime)) {
            setError(
              `paperspace.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.paperspace.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.paperspace.autoscaler.unreadyTime)) {
            setError(
              `paperspace.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.paperspace.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.paperspace.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `paperspace.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.paperspace.nodePools) {
          const index = Number(indexStr);
          const np = v.paperspace.nodePools[index];

          if (!np.name) {
            setError(`paperspace.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`paperspace.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`paperspace.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          // Check name uniqueness across all pool types
          if (np.name && allPoolNames.filter((n) => n === np.name).length > 1) {
            setError(`paperspace.nodePools.${index}.name`, `Name is not unique`);
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`paperspace.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`paperspace.nodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(`paperspace.nodePools.${index}.labels.${_index}.value`, `Value can be at most 63 characters`);
            }
          }

          const taintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`paperspace.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`paperspace.nodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              taintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(`paperspace.nodePools.${index}.taints.${_index}.value`, `Value can be at most 63 characters`);
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `paperspace.nodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          const uniqueTaintKeys = new Set(taintKeys);
          if (taintKeys.length !== uniqueTaintKeys.size) {
            setError(`paperspace.nodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }

          if (np.minSize.length < 1) {
            setError(`paperspace.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`paperspace.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`paperspace.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`paperspace.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`paperspace.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (!np.publicIpType) {
            setError(`paperspace.nodePools.${index}.publicIpType`, `Public IP Type is required`);
          }

          if (!np.machineType) {
            setError(`paperspace.nodePools.${index}.machineType`, `Machine Type is required`);
          }

          if (!np.bootDiskSize) {
            setError(`paperspace.nodePools.${index}.bootDiskSize`, `Boot Disk Size is required`);
          } else {
            if (![50, 100, 250, 500, 1000, 2000].includes(Number(np.bootDiskSize))) {
              setError(
                `paperspace.nodePools.${index}.bootDiskSize`,
                `Boot Disk Size must be one of these values: [50, 100, 250, 500, 1000, 2000]`,
              );
            }
          }
        }

        for (const indexStr in v.paperspace.unmanagedNodePools) {
          const index = Number(indexStr);
          const np = v.paperspace.unmanagedNodePools[index];

          if (!np.name) {
            setError(`paperspace.unmanagedNodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`paperspace.unmanagedNodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`paperspace.unmanagedNodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          // Check name uniqueness across all pool types
          if (np.name && allPoolNames.filter((n) => n === np.name).length > 1) {
            setError(`paperspace.unmanagedNodePools.${index}.name`, `Name is not unique`);
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`paperspace.unmanagedNodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`paperspace.unmanagedNodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(`paperspace.unmanagedNodePools.${index}.labels.${_index}.value`, `Value can be at most 63 characters`);
            }
          }

          const unmanagedTaintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`paperspace.unmanagedNodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`paperspace.unmanagedNodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              unmanagedTaintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(`paperspace.unmanagedNodePools.${index}.taints.${_index}.value`, `Value can be at most 63 characters`);
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `paperspace.unmanagedNodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          const uniqueUnmanagedTaintKeys = new Set(unmanagedTaintKeys);
          if (unmanagedTaintKeys.length !== uniqueUnmanagedTaintKeys.size) {
            setError(`paperspace.unmanagedNodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }
        }
      }

      if (v.provider === "triton") {
        if (!v.triton.location) {
          setError(`triton.location`, "Location is required");
        }

        if (!v.triton.privateNetworkId) {
          setError(`triton.privateNetworkId`, "Private Network ID is required");
        }

        if (!v.triton.imageId) {
          setError(`triton.imageId`, "Image ID is required");
        }

        if (v.triton.sshKeys.length > 0) {
          const sshKeyValues: string[] = [];
          for (const indexStr in v.triton.sshKeys) {
            const index = Number(indexStr);
            const item = v.triton.sshKeys[index];

            if (!item.value) {
              setError(`triton.sshKeys.${index}.value`, `Value is required`);
              setError(`triton.sshKeysroot`, `SSH Key values are required`);
            } else {
              sshKeyValues.push(item.value);
              const validStarterStringsRaw: string[] = [
                "ssh-rsa",
                "ssh-ed25519",
                "ecdsa-sha2-nistp256",
                "ssh-ecdsa",
                "ssh-dsa",
              ];

              const validStarterStrings = validStarterStringsRaw.map((v) => `${v} `);

              let startsWithValidString = false;
              for (const validStarterString of validStarterStrings) {
                if (item.value.startsWith(validStarterString)) {
                  startsWithValidString = true;
                }
              }

              if (!startsWithValidString) {
                setError(
                  `triton.sshKeys.${index}.value`,
                  `Value must start with one of [${validStarterStringsRaw.join(", ")}] values`,
                );
                setError(
                  `triton.sshKeysroot`,
                  `Values must start with one of [${validStarterStringsRaw.join(", ")}] values`,
                );
              }
            }
          }
          const uniqueSshKeys = new Set(sshKeyValues);
          if (sshKeyValues.length !== uniqueSshKeys.size) {
            setError(`triton.sshKeysroot`, `SSH Keys must be unique`);
          }
        }

        if (!v.triton.connection.url) {
          setError(`triton.connection.url`, "URL is required");
        } else {
          if (!v.triton.connection.url.startsWith(`https`)) {
            setError(`triton.connection.url`, "URL must be https");
          }
        }

        if (!v.triton.connection.account) {
          setError(`triton.connection.account`, "Account is required");
        }

        if (!v.triton.connection.privateKeySecretLink.link) {
          setError(`triton.connection.privateKeySecretLink`, "Private Key Secret is required");
        } else {
          const parsedLink = parseLink(v.triton.connection.privateKeySecretLink.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`triton.connection.privateKeySecretLink`, parsedLink.errorMessage);
          }
        }

        if (v.triton.loadBalancer.type === "manual") {
          if (!v.triton.loadBalancer.manual.packageId) {
            setError(`triton.loadBalancer.manual.packageId`, "Package ID is required");
          }
          if (!v.triton.loadBalancer.manual.imageId) {
            setError(`triton.loadBalancer.manual.imageId`, "Image ID is required");
          }
          if (!v.triton.loadBalancer.manual.publicNetworkId) {
            setError(`triton.loadBalancer.manual.publicNetworkId`, "Public Network ID is required");
          }
          if (!v.triton.loadBalancer.manual.cnsInternalDomain) {
            setError(`triton.loadBalancer.manual.cnsInternalDomain`, "CNS Internal Domain is required");
          }
          if (!v.triton.loadBalancer.manual.cnsPublicDomain) {
            setError(`triton.loadBalancer.manual.cnsPublicDomain`, "CNS Public Domain is required");
          }

          if (!v.triton.loadBalancer.manual.count) {
            setError(`triton.loadBalancer.manual.count`, "Count is required");
          } else {
            const xn = Number(v.triton.loadBalancer.manual.count);
            if (xn < 1) {
              setError(`triton.loadBalancer.manual.count`, "Count must be at least 1");
            }
            if (xn > 3) {
              setError(`triton.loadBalancer.manual.count`, "Count can be at most 3");
            }
          }

          if (v.triton.loadBalancer.manual.logging.nodePort.length > 0) {
            const xn = Number(v.triton.loadBalancer.manual.logging.nodePort);
            if (xn < 30000) {
              setError(`triton.loadBalancer.manual.logging.nodePort`, "Node Port must be at least 30000");
            }
            if (xn > 65535) {
              setError(`triton.loadBalancer.manual.logging.nodePort`, "Node Port can be at most 65535");
            }
          }

          if (v.triton.loadBalancer.manual.logging.externalSyslog) {
            const syslogValue = v.triton.loadBalancer.manual.logging.externalSyslog;
            const parts = syslogValue.split(":");
            if (parts.length !== 2) {
              setError(`triton.loadBalancer.manual.logging.externalSyslog`, "Must be in format host:port");
            } else {
              const [host, portStr] = parts;
              if (!host || host.trim().length === 0) {
                setError(`triton.loadBalancer.manual.logging.externalSyslog`, "Host cannot be empty");
              } else if (host.includes(" ") || host.startsWith(".") || host.endsWith(".")) {
                setError(`triton.loadBalancer.manual.logging.externalSyslog`, "Invalid host format");
              }
              const port = parseInt(portStr, 10);
              if (isNaN(port) || portStr !== port.toString()) {
                setError(`triton.loadBalancer.manual.logging.externalSyslog`, "Port must be a valid number");
              } else if (port < 1 || port > 65535) {
                setError(`triton.loadBalancer.manual.logging.externalSyslog`, "Port must be between 1 and 65535");
              }
            }
          }

          // Check privateNetworkIds uniqueness
          const manualPrivateNetworkIds = v.triton.loadBalancer.manual.privateNetworkIds
            .map((n: { id: string; value: string }) => n.value)
            .filter(Boolean);
          const uniqueManualPrivateNetworkIds = new Set(manualPrivateNetworkIds);
          if (manualPrivateNetworkIds.length !== uniqueManualPrivateNetworkIds.size) {
            setError(`triton.loadBalancer.manual.privateNetworkIds`, `Private Network IDs must be unique`);
          }

          for (const indexStr in v.triton.loadBalancer.manual.metadata) {
            const index = Number(indexStr);
            const item = v.triton.loadBalancer.manual.metadata[index];

            if (!item.name) {
              setError(`triton.loadBalancer.manual.metadata.${index}.name`, `Key is required`);
              setError(`triton.loadBalancer.manual.metadataroot`, `Keys are required`);
            } else {
              if (item.name.length > 63) {
                setError(`triton.loadBalancer.manual.metadata.${index}.name`, `Key can be at most 63 characters`);
                setError(`triton.loadBalancer.manual.metadataroot`, `Keys can be at most 63 characters`);
              }

              const regex = /^[:a-zA-Z0-9_.-]+$/;
              if (!regex.test(item.name)) {
                setError(
                  `triton.loadBalancer.manual.metadata.${index}.name`,
                  `Key must be a valid match for regex: ${regex}`,
                );
                setError(`triton.loadBalancer.manual.metadataroot`, `Keys must match regex: ${regex}`);
              }
            }
          }

          for (const indexStr in v.triton.loadBalancer.manual.tags) {
            const index = Number(indexStr);
            const item = v.triton.loadBalancer.manual.tags[index];

            if (!item.name) {
              setError(`triton.loadBalancer.manual.tags.${index}.name`, `Key is required`);
              setError(`triton.loadBalancer.manual.tagsroot`, `Keys are required`);
            } else {
              if (item.name.length > 63) {
                setError(`triton.loadBalancer.manual.tags.${index}.name`, `Key can be at most 63 characters`);
                setError(`triton.loadBalancer.manual.tagsroot`, `Keys can be at most 63 characters`);
              }

              const regex = /^[:a-zA-Z0-9_.-]+$/;
              if (!regex.test(item.name)) {
                setError(
                  `triton.loadBalancer.manual.tags.${index}.name`,
                  `Key must be a valid match for regex: ${regex}`,
                );
                setError(`triton.loadBalancer.manual.tagsroot`, `Keys must match regex: ${regex}`);
              }
            }

            if (!item.value.length) {
              setError(`triton.loadBalancer.manual.tags.${index}.value`, `Value is required`);
              setError(`triton.loadBalancer.manual.tagsroot`, `Values are required`);
            } else if (item.value.length > 255) {
              setError(`triton.loadBalancer.manual.tags.${index}.value`, `Value can be at most 255 characters`);
              setError(`triton.loadBalancer.manual.tagsroot`, `Values can be at most 255 characters`);
            }
          }
        }

        if (v.triton.preInstallScript) {
          if (!v.triton.preInstallScript.startsWith("#!") && !v.triton.preInstallScript.startsWith("resolve:ssm:")) {
            setError(
              `triton.preInstallScript`,
              `Pre Install Script must start with either "#!" or with "resolve:ssm:"`,
            );
          }
        }

        if (v.triton.networking.dnsForwarder) {
          if (v.triton.networking.dnsForwarder.length > 100) {
            setError(`triton.networking.dnsForwarder`, `DNS Forwarder can be at most 100 characters`);
          }
        }

        if (v.triton.autoscaler.expander.length < 1) {
          setError(`triton.autoscaler.expander`, `At least 1 expander is required`);
        } else if (v.triton.autoscaler.expander.length > 3) {
          setError(`triton.autoscaler.expander`, `Expander list can have at most 3 selections`);
        } else {
          const uniqueExpanders = new Set(v.triton.autoscaler.expander);
          if (uniqueExpanders.size !== v.triton.autoscaler.expander.length) {
            setError(`triton.autoscaler.expander`, `Expanders must be unique`);
          }
        }

        if (v.triton.autoscaler.unneededTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.triton.autoscaler.unneededTime)) {
            setError(
              `triton.autoscaler.unneededTime`,
              `Unneeded Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.triton.autoscaler.unreadyTime.length > 0) {
          const regex = /^(\d+(?:\.\d+)?)([hms]+)$/;
          if (!regex.test(v.triton.autoscaler.unreadyTime)) {
            setError(
              `triton.autoscaler.unreadyTime`,
              `Unready Time must be a GO Duration string, and a valid match for regex: ${regex}`,
            );
          }
        }

        if (v.triton.autoscaler.utilizationThreshold.length > 0) {
          const xn = Number(v.triton.autoscaler.utilizationThreshold);
          if (xn < 0.1 || xn > 1) {
            setError(
              `triton.autoscaler.utilizationThreshold`,
              `Utilization Threshold can be between the values 0.1 and 1`,
            );
          }
        }

        for (const indexStr in v.triton.nodePools) {
          const index = Number(indexStr);
          const np = v.triton.nodePools[index];

          if (!np.name) {
            setError(`triton.nodePools.${index}.name`, `Name is required`);
          } else {
            if (np.name.length > 63) {
              setError(`triton.nodePools.${index}.name`, `Name can be at most 63 characters`);
            }

            const regex = /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/;
            if (!regex.test(np.name)) {
              setError(`triton.nodePools.${index}.name`, `Name must be a valid match for regex: ${regex}`);
            }
          }

          for (const _indexStr in v.triton.nodePools) {
            const _index = Number(_indexStr);

            if (index === _index) {
              continue;
            }

            const _np = v.triton.nodePools[_index];

            if (!!_np.name && !!np.name && _np.name === np.name) {
              setError(`triton.nodePools.${index}.name`, `Name is not unique`);
            }
          }

          for (const _indexStr in np.labels) {
            const _index = Number(_indexStr);
            const item = np.labels[_index];

            if (!item.name) {
              setError(`triton.nodePools.${index}.labels.${_index}.name`, `Key is required`);
              setError(`triton.nodePools.${index}.labelsroot`, `Keys are required`);
            }
            if (item.value && item.value.length > 63) {
              setError(`triton.nodePools.${index}.labels.${_index}.value`, `Value can be at most 63 characters`);
            }
          }

          const taintKeys: string[] = [];
          for (const _indexStr in np.taints) {
            const _index = Number(_indexStr);
            const item = np.taints[_index];

            if (!item.key) {
              setError(`triton.nodePools.${index}.taints.${_index}.key`, `Key is required`);
              setError(`triton.nodePools.${index}.taintsroot`, `Keys are required`);
            } else {
              taintKeys.push(item.key);
            }
            if (item.value && item.value.length > 63) {
              setError(`triton.nodePools.${index}.taints.${_index}.value`, `Value can be at most 63 characters`);
            }
            if (item.effect && !["NoSchedule", "PreferNoSchedule", "NoExecute"].includes(item.effect)) {
              setError(
                `triton.nodePools.${index}.taints.${_index}.effect`,
                `Effect must be NoSchedule, PreferNoSchedule, or NoExecute`,
              );
            }
          }
          const uniqueTaintKeys = new Set(taintKeys);
          if (taintKeys.length !== uniqueTaintKeys.size) {
            setError(`triton.nodePools.${index}.taintsroot`, `Taint keys must be unique`);
          }

          // Check privateNetworkIds uniqueness
          const privateNetworkIds = np.privateNetworkIds
            .map((n: { id: string; value: string }) => n.value)
            .filter(Boolean);
          const uniquePrivateNetworkIds = new Set(privateNetworkIds);
          if (privateNetworkIds.length !== uniquePrivateNetworkIds.size) {
            setError(`triton.nodePools.${index}.privateNetworkIds`, `Private Network IDs must be unique`);
          }

          // Validate tritonTags
          for (const _indexStr in np.tritonTags) {
            const _index = Number(_indexStr);
            const item = np.tritonTags[_index];

            if (!item.name) {
              setError(`triton.nodePools.${index}.tritonTags.${_index}.name`, `Key is required`);
              setError(`triton.nodePools.${index}.tritonTagsroot`, `Keys are required`);
            } else {
              if (item.name.length > 63) {
                setError(`triton.nodePools.${index}.tritonTags.${_index}.name`, `Key can be at most 63 characters`);
              }
              const regex = /^[:a-zA-Z0-9_.-]+$/;
              if (!regex.test(item.name)) {
                setError(
                  `triton.nodePools.${index}.tritonTags.${_index}.name`,
                  `Key must be a valid match for regex: ${regex}`,
                );
              }
            }

            if (!item.value || item.value.length < 1) {
              setError(`triton.nodePools.${index}.tritonTags.${_index}.value`, `Value is required`);
              setError(`triton.nodePools.${index}.tritonTagsroot`, `Values are required`);
            } else if (item.value.length > 255) {
              setError(`triton.nodePools.${index}.tritonTags.${_index}.value`, `Value can be at most 255 characters`);
            }
          }

          if (np.minSize.length < 1) {
            setError(`triton.nodePools.${index}.minSize`, `Min Size is required`);
          }
          if (np.maxSize.length < 1) {
            setError(`triton.nodePools.${index}.maxSize`, `Max Size is required`);
          }
          if (np.minSize.length > 0 && np.maxSize.length > 0) {
            const minSizeNumber = Number(np.minSize);
            const maxSizeNumber = Number(np.maxSize);

            if (minSizeNumber < 0) {
              setError(`triton.nodePools.${index}.minSize`, `Min Size must be at least 0`);
            }
            if (minSizeNumber > maxSizeNumber) {
              setError(`triton.nodePools.${index}.minSize`, `Min Size cannot be higher than Max Size`);
            }
            if (maxSizeNumber < minSizeNumber) {
              setError(`triton.nodePools.${index}.maxSize`, `Max Size cannot be lower than Min Size`);
            }
          }

          if (!np.packageId) {
            setError(`triton.nodePools.${index}.packageId`, `Package ID is required`);
          }
        }
      }

      if (v.addons.awsECR.isEnabled) {
        if (!v.addons.awsECR.roleArn) {
          if (v.provider !== "aws") {
            setError(`addons.awsECR.roleArn`, `Role ARN is required`);
          }
        } else {
          const regex = /^arn:(aws):iam::[0-9]+:role\/.+/;
          if (!regex.test(v.addons.awsECR.roleArn)) {
            setError(`addons.awsECR.roleArn`, `Role ARN must be a valid match for regex: ${regex}`);
          }
        }
      }

      if (v.addons.awsEFS.isEnabled) {
        if (!v.addons.awsEFS.roleArn) {
          setError(`addons.awsEFS.roleArn`, `Role ARN is required`);
        } else {
          const regex = /^arn:(aws):iam::[0-9]+:role\/.+/;
          if (!regex.test(v.addons.awsEFS.roleArn)) {
            setError(`addons.awsEFS.roleArn`, `Role ARN must be a valid match for regex: ${regex}`);
          }
        }
      }

      if (v.addons.awsELB.isEnabled) {
        if (v.addons.awsELB.roleArn.length > 0) {
          const regex = /^arn:(aws):iam::[0-9]+:role\/.+/;
          if (!regex.test(v.addons.awsELB.roleArn)) {
            setError(`addons.awsELB.roleArn`, `Role ARN must be a valid match for regex: ${regex}`);
          }
        }
      }

      if (v.addons.metrics.isEnabled) {
        if (v.addons.metrics.scrapeAnnotated.isEnabled) {
          if (v.addons.metrics.scrapeAnnotated.intervalSeconds.length > 0) {
            const xn = Number(v.addons.metrics.scrapeAnnotated.intervalSeconds);
            if (xn < 30) {
              setError(`addons.metrics.scrapeAnnotated.intervalSeconds`, `Interval must be at least 30 seconds`);
            }
          }
          // Validate regex fields
          const metricsRegexFields = ["includeNamespaces", "excludeNamespaces", "retainLabels"] as const;
          for (const field of metricsRegexFields) {
            const value = v.addons.metrics.scrapeAnnotated[field];
            if (value) {
              try {
                new RegExp(value);
              } catch {
                setError(`addons.metrics.scrapeAnnotated.${field}`, `Must be a valid regular expression`);
              }
            }
          }
        }
      }

      if (v.addons.logs.isEnabled) {
        const logsRegexFields = ["includeNamespaces", "excludeNamespaces"] as const;
        for (const field of logsRegexFields) {
          const value = v.addons.logs[field];
          if (value) {
            try {
              new RegExp(value);
            } catch {
              setError(`addons.logs.${field}`, `Must be a valid regular expression`);
            }
          }
        }
      }

      if (v.addons.azureACR.isEnabled) {
        if (!v.addons.azureACR.clientId) {
          setError(`addons.azureACR.clientId`, `Client ID is required`);
        }
      }

      if (v.addons.byok.isEnabled) {
        if (!v.addons.byok.location.link) {
          setError(`addons.byok.location`, `Location is required`);
        }
      }

      for (const indexStr in v.firewall) {
        const index = Number(indexStr);
        const item = v.firewall[index];

        if (!item.sourceCIDR) {
          setError(`firewall.${index}.sourceCIDR`, `Source CIDR is required`);
          setError(`firewallroot`, `Source CIDR values are required`);
        }
      }

      if (Object.keys(e).length < 1) {
        // dryrun
        try {
          formSet(id, `meta.asyncValidating`, true);
          const org = store.getState().context.org;
          const ctx = { org, gvc: "" };
          const body = inputToMk8s(v);
          const method = v.meta.isCreate ? "post" : "patch";
          const url = v.meta.isCreate ? `${homeLink("mk8s", ctx)}?dryRun` : `${v.meta.clusterLink}?dryRun`;

          // For PATCH dry runs, modify description to ensure backend triggers validation
          // The backend may skip validation if no changes are detected
          if (!v.meta.isCreate) {
            body.description = `${body.description || v.name} [dryrun:${validationCallId.slice(0, 8)}]`;
          }

          const res = await request({
            method: method,
            url: url,
            body: body,
            dontThrowOnFailure: true,
          });

          // Process dry run response for both errors (400) and warnings (200 with issues)
          if (res.status === 400 || res.data?.details?.issues) {
            const dryRun = processDryRunResponse(res.data);
            for (const issue of dryRun.details.issues) {
              // Normalize array bracket notation [0] to dot notation .0
              // Must happen before prefix stripping so spec.firewall[1] becomes spec.firewall.1 first
              issue.path = issue.path.replace(/\[(\d+)\]/g, ".$1");

              issue.path = issue.path.replace("spec.provider.", "");
              issue.path = issue.path.replace("spec.addOns.", "addons.");
              issue.path = issue.path.replace("spec.firewall.", "firewall.");

              // Handle direct errors on spec.addOns or spec.firewall
              if (issue.path === "spec.addOns") {
                issue.path = "addonsroot";
              }
              if (issue.path === "spec.firewall") {
                issue.path = "firewallroot";
              }

              // Handle spec.version -> specVersion rename
              if (issue.path === "spec.version") {
                issue.path = "specVersion";
              }

              // ArrayString paths need .value suffix
              // Format: { prefix: string, depth: number }
              // depth is how many parts the path should have (prefix + index)
              const arrayStringPaths = [
                // AWS provider-level
                { prefix: "aws.securityGroupIds.", depth: 3 },
                { prefix: "aws.extraNodePolicies.", depth: 3 },
                // AWS nodePools nested
                { prefix: "aws.nodePools.", depth: 5, nestedField: "instanceTypes" },
                { prefix: "aws.nodePools.", depth: 5, nestedField: "subnetIds" },
                { prefix: "aws.nodePools.", depth: 5, nestedField: "extraSecurityGroupIds" },
                // Azure
                { prefix: "azure.sshKeys.", depth: 3 },
                { prefix: "azure.nodePools.", depth: 5, nestedField: "zones" },
                // GCP
                { prefix: "gcp.tags.", depth: 3 },
                // DigitalOcean
                { prefix: "digitalocean.sshKeys.", depth: 3 },
                { prefix: "digitalocean.extraSshKeys.", depth: 3 },
                { prefix: "digitalocean.reservedIPs.", depth: 3 },
                // Linode
                { prefix: "linode.authorizedUsers.", depth: 3 },
                { prefix: "linode.authorizedKeys.", depth: 3 },
                // Lambdalabs
                { prefix: "lambdalabs.fileSystems.", depth: 3 },
                // Oblivus
                { prefix: "oblivus.sshKeys.", depth: 3 },
                // Paperspace
                { prefix: "paperspace.sharedDrives.", depth: 3 },
                { prefix: "paperspace.userIds.", depth: 3 },
                // Triton
                { prefix: "triton.sshKeys.", depth: 3 },
                { prefix: "triton.nodePools.", depth: 5, nestedField: "privateNetworkIds" },
                { prefix: "triton.loadBalancer.manual.privateNetworkIds.", depth: 5 },
              ];

              let arrayStringRootPath: string | null = null;
              for (const config of arrayStringPaths) {
                if (config.nestedField) {
                  // Handle nested arrays like aws.nodePools.0.instanceTypes.0
                  const nestedPattern = config.prefix;
                  if (issue.path.startsWith(nestedPattern)) {
                    const parts = issue.path.split(".");
                    // Check if pattern matches: provider.nodePools.{index}.{nestedField}.{index}
                    if (
                      parts.length === config.depth &&
                      parts[3] === config.nestedField &&
                      !isNaN(Number(parts[2])) &&
                      !isNaN(Number(parts[4]))
                    ) {
                      issue.path = issue.path + ".value";
                      // Root path: aws.nodePools.0.instanceTypesroot
                      arrayStringRootPath =
                        parts[0] + "." + parts[1] + "." + parts[2] + "." + config.nestedField + "root";
                      break;
                    }
                    // Also handle array-level errors like aws.nodePools.0.instanceTypes (4 parts, no item index)
                    // This happens when the API returns an error for the whole array, not a specific item
                    if (
                      parts.length === config.depth - 1 &&
                      parts[3] === config.nestedField &&
                      !isNaN(Number(parts[2]))
                    ) {
                      // Root path: aws.nodePools.0.instanceTypesroot
                      arrayStringRootPath =
                        parts[0] + "." + parts[1] + "." + parts[2] + "." + config.nestedField + "root";
                      break;
                    }
                  }
                } else {
                  // Handle simple arrays like aws.securityGroupIds.0
                  if (issue.path.startsWith(config.prefix)) {
                    const parts = issue.path.split(".");
                    if (parts.length === config.depth && !isNaN(Number(parts[config.depth - 1]))) {
                      issue.path = issue.path + ".value";
                      // Root path: aws.securityGroupIdsroot
                      arrayStringRootPath = parts.slice(0, config.depth - 1).join(".") + "root";
                      break;
                    }
                  }
                }
              }
              if (arrayStringRootPath) {
                (issue as any).rootPath = arrayStringRootPath;
              }

              // Object to Array conversion (Record<string, string> -> NameValuePair[])
              // Entity path like "aws.awsTags.myKey" becomes "aws.awsTagsroot"
              // Format: { prefix: string, field: string, depth: number }
              // For root-level: depth 2 (e.g., tags.keyName)
              // For provider-level: depth 3 (e.g., aws.awsTags.keyName)
              // For nodePool-level: depth 5 (e.g., aws.nodePools.0.labels.keyName)
              const objectToArrayPaths = [
                // Root-level tags (spec.tags.keyName -> tagsroot)
                { prefix: "spec.tags.", depth: 2, field: "tags" },
                // AWS
                { prefix: "aws.awsTags.", depth: 3, field: "awsTags" },
                { prefix: "aws.nodePools.", depth: 5, field: "labels" },
                // Azure
                { prefix: "azure.tags.", depth: 3, field: "tags" },
                { prefix: "azure.nodePools.", depth: 5, field: "labels" },
                // GCP
                { prefix: "gcp.labels.", depth: 3, field: "labels" },
                { prefix: "gcp.metadata.", depth: 3, field: "metadata" },
                { prefix: "gcp.nodePools.", depth: 5, field: "labels" },
                // DigitalOcean
                { prefix: "digitalocean.digitalOceanTags.", depth: 3, field: "digitalOceanTags" },
                { prefix: "digitalocean.nodePools.", depth: 5, field: "labels" },
                // Hetzner
                { prefix: "hetzner.hetznerLabels.", depth: 3, field: "hetznerLabels" },
                { prefix: "hetzner.floatingIPSelector.", depth: 3, field: "floatingIPSelector" },
                { prefix: "hetzner.nodePools.", depth: 5, field: "labels" },
                { prefix: "hetzner.dedicatedServerNodePools.", depth: 5, field: "labels" },
                // Lambdalabs
                { prefix: "lambdalabs.nodePools.", depth: 5, field: "labels" },
                { prefix: "lambdalabs.unmanagedNodePools.", depth: 5, field: "labels" },
                // Linode
                { prefix: "linode.nodePools.", depth: 5, field: "labels" },
                // Oblivus
                { prefix: "oblivus.nodePools.", depth: 5, field: "labels" },
                { prefix: "oblivus.unmanagedNodePools.", depth: 5, field: "labels" },
                // Paperspace
                { prefix: "paperspace.nodePools.", depth: 5, field: "labels" },
                { prefix: "paperspace.unmanagedNodePools.", depth: 5, field: "labels" },
                // Triton
                { prefix: "triton.nodePools.", depth: 5, field: "labels" },
                { prefix: "triton.nodePools.", depth: 5, field: "tritonTags" },
                // Triton loadBalancer.manual paths (not nodePool-level, use depth 4 for fixed prefix)
                { prefix: "triton.loadBalancer.manual.metadata.", depth: 4, field: "metadata" },
                { prefix: "triton.loadBalancer.manual.tags.", depth: 4, field: "tags" },
                // Ephemeral
                { prefix: "ephemeral.nodePools.", depth: 5, field: "labels" },
                // Generic
                { prefix: "generic.nodePools.", depth: 5, field: "labels" },
              ];

              for (const config of objectToArrayPaths) {
                if (issue.path.startsWith(config.prefix)) {
                  const parts = issue.path.split(".");
                  if (config.depth === 2) {
                    // Root-level: spec.tags.keyName -> tagsroot
                    if (parts.length >= 2) {
                      issue.path = config.field + "root";
                      break;
                    }
                  } else if (config.depth === 3) {
                    // Provider-level: aws.awsTags.keyName -> aws.awsTagsroot
                    if (parts.length >= 3) {
                      issue.path = parts[0] + "." + config.field + "root";
                      break;
                    }
                  } else if (config.depth === 4) {
                    // Fixed nested path: triton.loadBalancer.manual.metadata.keyName -> triton.loadBalancer.manual.metadataroot
                    if (parts.length >= 5 && parts[3] === config.field) {
                      issue.path = parts[0] + "." + parts[1] + "." + parts[2] + "." + config.field + "root";
                      break;
                    }
                  } else if (config.depth === 5) {
                    // NodePool-level: aws.nodePools.0.labels.keyName -> aws.nodePools.0.labelsroot
                    if (parts.length >= 5 && parts[3] === config.field && !isNaN(Number(parts[2]))) {
                      issue.path = parts[0] + "." + parts[1] + "." + parts[2] + "." + config.field + "root";
                      break;
                    }
                  }
                }
              }

              // Array of objects - set root error for arrays that already have field paths
              // Example: aws.nodePools.0.name -> also set aws.nodePoolsroot
              // Example: aws.nodePools.0.taints.0.key -> also set aws.nodePools.0.taintsroot
              // Format: { prefix: string, depth: number, field?: string }
              // depth is the position of the array index in the path
              const arrayOfObjectsPaths = [
                // Provider-level arrays (depth 2: provider.arrayName.index.field)
                { prefix: "aws.deployRoleChain.", depth: 2 },
                { prefix: "aws.nodePools.", depth: 2 },
                { prefix: "azure.nodePools.", depth: 2 },
                { prefix: "gcp.nodePools.", depth: 2 },
                { prefix: "digitalocean.nodePools.", depth: 2 },
                { prefix: "hetzner.nodePools.", depth: 2 },
                { prefix: "hetzner.dedicatedServerNodePools.", depth: 2 },
                { prefix: "linode.nodePools.", depth: 2 },
                { prefix: "lambdalabs.nodePools.", depth: 2 },
                { prefix: "lambdalabs.unmanagedNodePools.", depth: 2 },
                { prefix: "oblivus.nodePools.", depth: 2 },
                { prefix: "oblivus.unmanagedNodePools.", depth: 2 },
                { prefix: "paperspace.nodePools.", depth: 2 },
                { prefix: "paperspace.unmanagedNodePools.", depth: 2 },
                { prefix: "triton.nodePools.", depth: 2 },
                { prefix: "ephemeral.nodePools.", depth: 2 },
                { prefix: "generic.nodePools.", depth: 2 },
                { prefix: "firewall.", depth: 1 },
                // Addons arrays (spec.addOns -> addons after replacement)
                { prefix: "addons.registryMirror.mirrors.", depth: 4 },
                // Nested taints in nodePools (depth 4: provider.nodePools.index.taints.index.field)
                { prefix: "aws.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "azure.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "gcp.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "digitalocean.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "hetzner.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "hetzner.dedicatedServerNodePools.", depth: 4, nestedField: "taints" },
                { prefix: "linode.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "lambdalabs.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "lambdalabs.unmanagedNodePools.", depth: 4, nestedField: "taints" },
                { prefix: "oblivus.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "oblivus.unmanagedNodePools.", depth: 4, nestedField: "taints" },
                { prefix: "paperspace.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "paperspace.unmanagedNodePools.", depth: 4, nestedField: "taints" },
                { prefix: "triton.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "ephemeral.nodePools.", depth: 4, nestedField: "taints" },
                { prefix: "generic.nodePools.", depth: 4, nestedField: "taints" },
              ];

              if (!(issue as any).rootPath) {
                for (const config of arrayOfObjectsPaths) {
                  if (issue.path.startsWith(config.prefix)) {
                    const parts = issue.path.split(".");
                    if (config.nestedField) {
                      // Nested array: aws.nodePools.0.taints.0.key -> aws.nodePools.0.taintsroot
                      if (
                        parts.length > config.depth + 1 &&
                        parts[3] === config.nestedField &&
                        !isNaN(Number(parts[2])) &&
                        !isNaN(Number(parts[4]))
                      ) {
                        (issue as any).rootPath =
                          parts[0] + "." + parts[1] + "." + parts[2] + "." + config.nestedField + "root";
                        break;
                      }
                    } else if (config.depth === 1) {
                      // Top-level array: firewall.0.sourceCIDR -> firewallroot
                      if (parts.length > 2 && !isNaN(Number(parts[1]))) {
                        (issue as any).rootPath = parts[0] + "root";
                        break;
                      }
                    } else if (config.depth === 4) {
                      // Nested array without nestedField: addons.registryMirror.mirrors.0.registry -> addons.registryMirror.mirrorsroot
                      if (parts.length > 4 && !isNaN(Number(parts[3]))) {
                        (issue as any).rootPath = parts[0] + "." + parts[1] + "." + parts[2] + "root";
                        break;
                      }
                    } else {
                      // Provider-level array: aws.nodePools.0.name -> aws.nodePoolsroot
                      if (parts.length > config.depth + 1 && !isNaN(Number(parts[config.depth]))) {
                        (issue as any).rootPath = parts.slice(0, config.depth).join(".") + "root";
                        break;
                      }
                    }
                  }
                }
              }
            }

            for (const issue of dryRun.details.issues) {
              const rootPath = (issue as any).rootPath as string | undefined;
              if (issue.severity === "error") {
                setError(issue.path, issue.message);
                if (rootPath) {
                  setError(rootPath, issue.message);
                }
              } else {
                setWarning(issue.path, issue.message);
                if (rootPath) {
                  setWarning(rootPath, issue.message);
                }
              }
            }

            formSet(id, `meta.submitted`, true);
          }

          const form = store.getState().form.instances[id];
          const _validationCallId = form.meta.validationCallId;
          if (_validationCallId !== validationCallId) {
            return null;
          }
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          console.error("Failed to process dryrun", errorMessage);
        }
      }

      // setup
      for (const path of Object.keys(e)) {
        const value = e[path];
        formSet(id, `errors.${path}`, value);
      }
      for (const path of Object.keys(w)) {
        const value = w[path];
        formSet(id, `warnings.${path}`, value);
      }

      const hasErrors = Object.keys(e).length > 0;

      formSet(id, `meta.valid`, !hasErrors);
      formSet(id, `meta.validating`, false);
      formSet(id, `meta.asyncValidating`, false);

      return hasErrors;
    } catch (e) {
      formSet(id, `errors`, { root: e.message });
      return true;
    }
  };
}
